// src/resolve/patterns.ts
var RE_STANDALONE_REF = /^\{([^{}]+)\}$/;
function createEmbeddedRefsGlobal() {
  return /\{([^{}]+)\}/g;
}
var RE_ANY_REF = /^\{.+\}$/;
function hasEmbeddedRefs(value) {
  return value.includes("{");
}
function parseTokenReferenceContent(content) {
  const match = /^([a-zA-Z][\w-]*)\.(.+?)(?:\/(\d{1,3}))?$/.exec(content.trim());
  if (!match) {
    return;
  }
  const [, category, path, opacitySource] = match;
  if (!category || !path) {
    return;
  }
  if (opacitySource === undefined) {
    return { category, path };
  }
  const opacity = Number(opacitySource);
  if (!Number.isInteger(opacity) || opacity < 0 || opacity > 100) {
    return;
  }
  return {
    category,
    path,
    opacity
  };
}
function parseStandaloneTokenReference(reference) {
  const match = RE_STANDALONE_REF.exec(reference);
  if (!match?.[1]) {
    return;
  }
  return parseTokenReferenceContent(match[1]);
}

// src/resolve/css-vars.ts
var CATEGORY_RENAME = {
  borders: "border",
  borderWidths: "border-width",
  borderStyles: "border-style",
  radii: "radius"
};
function toKebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z])([A-Z][a-z])/g, "$1-$2").toLowerCase();
}
function sanitizeCssVarSegment(segment) {
  return toKebabCase(segment).replace(/\//g, "-over-").replace(/[^a-z0-9-]/g, "-");
}
function tokenPathToCssVar(path, prefix = "--smth") {
  if (path.startsWith("typography.")) {
    const rest = path.slice(11);
    if (rest.startsWith("fontSize.")) {
      return `${prefix}-font-size-${rest.slice(9)}`;
    }
    if (rest.startsWith("lineHeight.")) {
      return `${prefix}-line-height-${rest.slice(11)}`;
    }
    if (rest.startsWith("fontWeight.")) {
      return `${prefix}-font-weight-${rest.slice(11)}`;
    }
    if (rest.startsWith("letterSpacing.")) {
      return `${prefix}-letter-spacing-${rest.slice(14)}`;
    }
    if (rest.startsWith("fontFamily.")) {
      return `${prefix}-font-${rest.slice(11)}`;
    }
  }
  const segments = path.split(".");
  const mapped = segments.map((segment) => sanitizeCssVarSegment(CATEGORY_RENAME[segment] ?? segment));
  if (mapped.length > 1) {
    const lastSegment = mapped[mapped.length - 1];
    if (lastSegment !== undefined) {
      mapped[mapped.length - 1] = sanitizeCssVarSegment(lastSegment);
    }
  }
  return `${prefix}-${mapped.join("-")}`;
}
function toCssVarRef(ref, prefix = "--smth") {
  const convertReferenceContent = (content) => {
    const parsed = parseTokenReferenceContent(content);
    if (!parsed) {
      return;
    }
    const tokenPath = `${parsed.category}.${parsed.path}`;
    const variableRef = `var(${tokenPathToCssVar(tokenPath, prefix)})`;
    if (parsed.opacity === undefined) {
      return variableRef;
    }
    return `color-mix(in srgb, ${variableRef} ${parsed.opacity}%, transparent)`;
  };
  const match = RE_STANDALONE_REF.exec(ref);
  const standaloneReference = match?.[1];
  if (standaloneReference !== undefined) {
    return convertReferenceContent(standaloneReference) ?? ref;
  }
  if (hasEmbeddedRefs(ref)) {
    return ref.replace(createEmbeddedRefsGlobal(), (_, path) => convertReferenceContent(path) ?? `{${path}}`);
  }
  return ref;
}

// src/tokens/spacing.ts
var SPACING_UNIT_REM = 0.25;
var POSITIVE_SPACING_STEPS = [
  0,
  0.5,
  1,
  1.5,
  2,
  2.5,
  3,
  3.5,
  4,
  4.5,
  5,
  5.5,
  6,
  6.5,
  7,
  7.5,
  8,
  8.5,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  20,
  24,
  25,
  28,
  32,
  36,
  37.5,
  40,
  44,
  48,
  50,
  52,
  56,
  60,
  64,
  72,
  80,
  96
];
var NEGATIVE_SPACING_STEPS = [
  -0.5,
  -1,
  -1.5,
  -2,
  -2.5,
  -3,
  -3.5,
  -4,
  -4.5,
  -5,
  -6,
  -7,
  -8,
  -9,
  -10,
  -11,
  -12,
  -13,
  -14,
  -15,
  -16,
  -20,
  -24,
  -28,
  -32,
  -36,
  -40,
  -44,
  -48,
  -52,
  -56,
  -60,
  -64,
  -72,
  -80,
  -96
];
function spacingValue(n) {
  const rem = n * SPACING_UNIT_REM;
  return `${rem}rem`;
}
function isSupportedSpacingStep(value) {
  return Number.isFinite(value);
}
var spacing = new Proxy(Object.create(null), {
  get(_target, prop) {
    if (typeof prop === "symbol")
      return;
    const n = Number(prop);
    if (!isSupportedSpacingStep(n))
      return;
    return spacingValue(n);
  },
  has(_target, prop) {
    if (typeof prop === "symbol")
      return false;
    const n = Number(prop);
    return isSupportedSpacingStep(n);
  },
  ownKeys() {
    return [];
  },
  getOwnPropertyDescriptor(_target, prop) {
    if (typeof prop === "symbol")
      return;
    const n = Number(prop);
    if (!isSupportedSpacingStep(n))
      return;
    return {
      configurable: true,
      enumerable: false,
      value: spacingValue(n)
    };
  }
});
var COMMON_SPACING_STEPS = [
  ...POSITIVE_SPACING_STEPS,
  ...NEGATIVE_SPACING_STEPS
];

// src/breakpoints.ts
var BREAKPOINT_ORDER = Object.freeze([
  "base",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl"
]);
var NON_BASE_BREAKPOINT_ORDER = Object.freeze(BREAKPOINT_ORDER.filter((breakpoint) => breakpoint !== "base"));
var BREAKPOINT_MIN_WIDTH = Object.freeze({
  sm: "40rem",
  md: "48rem",
  lg: "64rem",
  xl: "80rem",
  "2xl": "96rem"
});
var BREAKPOINT_SET = new Set(BREAKPOINT_ORDER);
function compareStrings(left, right) {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}
var BREAKPOINT_LENGTH_PATTERN = /^(-?(?:\d+\.?\d*|\.\d+))(px|rem|em|vw|vh|svw|svh|lvw|lvh|dvw|dvh)$/;
var sortedBreakpointScaleCache = new WeakMap;
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasBreakpointScaleShape(value) {
  return isPlainObject(value) && Object.values(value).every((entry) => typeof entry === "string");
}
function parseBreakpointLength(value) {
  const match = BREAKPOINT_LENGTH_PATTERN.exec(value.trim());
  if (!match) {
    return;
  }
  const numericValue = Number(match[1]);
  const unit = match[2];
  switch (unit) {
    case "px":
      return {
        category: "font-relative",
        value: numericValue
      };
    case "rem":
    case "em":
      return {
        category: "font-relative",
        value: numericValue * 16
      };
    case "vw":
    case "svw":
    case "lvw":
    case "dvw":
      return {
        category: "viewport-width",
        value: numericValue
      };
    case "vh":
    case "svh":
    case "lvh":
    case "dvh":
      return {
        category: "viewport-height",
        value: numericValue
      };
    default:
      return;
  }
}
function compareIndexedBreakpointEntries(left, right) {
  if (left.parsed && right.parsed && left.parsed.category === right.parsed.category) {
    if (left.parsed.value !== right.parsed.value) {
      return left.parsed.value - right.parsed.value;
    }
    const keyCompare = compareStrings(left.key, right.key);
    if (keyCompare !== 0) {
      return keyCompare;
    }
  }
  return left.index - right.index;
}
function normalizeBreakpointEntries(scale) {
  return Object.entries(scale).map(([key, value], index) => ({
    key,
    value,
    index,
    parsed: parseBreakpointLength(value)
  })).sort(compareIndexedBreakpointEntries);
}
function sortBreakpointScale(scale) {
  const cached = sortedBreakpointScaleCache.get(scale);
  if (cached) {
    return cached;
  }
  const sorted = Object.freeze(Object.fromEntries(normalizeBreakpointEntries(scale).map(({ key, value }) => [key, value])));
  sortedBreakpointScaleCache.set(scale, sorted);
  sortedBreakpointScaleCache.set(sorted, sorted);
  return sorted;
}
function resolveBreakpointScale(input) {
  if (!input) {
    return BREAKPOINT_MIN_WIDTH;
  }
  if (isPlainObject(input.breakpoints)) {
    return sortBreakpointScale(input.breakpoints);
  }
  if (hasBreakpointScaleShape(input)) {
    return sortBreakpointScale(input);
  }
  return BREAKPOINT_MIN_WIDTH;
}
function getNonBaseBreakpointOrder(input) {
  return Object.keys(resolveBreakpointScale(input));
}

// src/semantic/colors.ts
var lightSemanticColors = {
  accent: "{colors.neutral.800}",
  accentSubtle: "{colors.neutral.700}",
  accentEmphasized: "{colors.neutral.900}",
  accentMuted: "{colors.neutral.300}",
  neutralDefault: "{colors.neutral.200}",
  neutralSubtle: "{colors.neutral.300}",
  neutralEmphasized: "{colors.neutral.100}",
  background: "{colors.neutral.50}",
  surface1: "{colors.neutral.100}",
  surface2: "{colors.neutral.200}",
  surface3: "{colors.neutral.300}",
  backgroundAlpha32: "{colors.background/32}",
  surface1Alpha32: "{colors.surface1/32}",
  surface2Alpha32: "{colors.surface2/32}",
  surface3Alpha32: "{colors.surface3/32}",
  surfaceInverse: "{colors.neutral.900}",
  surfaceInverseAlpha32: "{colors.surfaceInverse/32}",
  surfaceOverlay: "{colors.neutral.900}",
  text: "{colors.neutral.900}",
  textSubtle: "{colors.neutral.700}",
  textDisabled: "{colors.neutral.500}",
  textInverse: "{colors.neutral.50}",
  textOnOverlay: "{colors.neutral.100}",
  textDark: "{colors.neutral.900}",
  textLight: "{colors.neutral.50}",
  borderSubtle: "{colors.neutral.300}",
  border: "{colors.neutral.400}",
  borderStrong: "{colors.neutral.500}",
  borderFocus: "{colors.neutral.800}",
  success: "{colors.green.500}",
  successSubtle: "{colors.green.400}",
  successEmphasized: "{colors.green.600}",
  successMuted: "{colors.green.100}",
  warning: "{colors.yellow.500}",
  warningSubtle: "{colors.yellow.400}",
  warningEmphasized: "{colors.yellow.600}",
  warningMuted: "{colors.yellow.100}",
  error: "{colors.red.600}",
  errorSubtle: "{colors.red.500}",
  errorEmphasized: "{colors.red.700}",
  errorMuted: "{colors.red.100}",
  info: "{colors.blue.500}",
  infoSubtle: "{colors.blue.400}",
  infoEmphasized: "{colors.blue.600}",
  infoMuted: "{colors.blue.100}"
};
var darkSemanticColors = {
  accent: "{colors.neutral.100}",
  accentSubtle: "{colors.neutral.200}",
  accentEmphasized: "{colors.neutral.50}",
  accentMuted: "{colors.neutral.600}",
  neutralDefault: "{colors.neutral.600}",
  neutralSubtle: "{colors.neutral.700}",
  neutralEmphasized: "{colors.neutral.500}",
  background: "{colors.neutral.900}",
  surface1: "{colors.neutral.800}",
  surface2: "{colors.neutral.700}",
  surface3: "{colors.neutral.600}",
  backgroundAlpha32: "{colors.background/32}",
  surface1Alpha32: "{colors.surface1/32}",
  surface2Alpha32: "{colors.surface2/32}",
  surface3Alpha32: "{colors.surface3/32}",
  surfaceInverse: "{colors.neutral.50}",
  surfaceInverseAlpha32: "{colors.surfaceInverse/32}",
  surfaceOverlay: "{colors.neutral.900}",
  text: "{colors.neutral.50}",
  textSubtle: "{colors.neutral.300}",
  textDisabled: "{colors.neutral.500}",
  textInverse: "{colors.neutral.900}",
  textOnOverlay: "{colors.neutral.100}",
  textDark: "{colors.neutral.900}",
  textLight: "{colors.neutral.50}",
  borderSubtle: "{colors.neutral.700}",
  border: "{colors.neutral.600}",
  borderStrong: "{colors.neutral.500}",
  borderFocus: "{colors.neutral.100}",
  success: "{colors.green.400}",
  successSubtle: "{colors.green.500}",
  successEmphasized: "{colors.green.300}",
  successMuted: "{colors.green.800}",
  warning: "{colors.yellow.400}",
  warningSubtle: "{colors.yellow.500}",
  warningEmphasized: "{colors.yellow.300}",
  warningMuted: "{colors.yellow.800}",
  error: "{colors.red.400}",
  errorSubtle: "{colors.red.500}",
  errorEmphasized: "{colors.red.300}",
  errorMuted: "{colors.red.800}",
  info: "{colors.blue.400}",
  infoSubtle: "{colors.blue.500}",
  infoEmphasized: "{colors.blue.300}",
  infoMuted: "{colors.blue.800}"
};
// src/tokens/borders.ts
var borders = Object.freeze({
  xs: "0.5px solid",
  sm: "1px solid",
  md: "2px solid",
  lg: "4px solid",
  xl: "8px solid"
});
var BORDER_TOKEN_SET = new Set(Object.keys(borders));
var BORDER_TOKEN_KEYS = Object.freeze(Object.keys(borders).sort());

// src/tokens/border-styles.ts
var borderStyles = Object.freeze({
  none: "none",
  solid: "solid",
  dashed: "dashed",
  dotted: "dotted",
  double: "double"
});
var BORDER_STYLE_TOKEN_SET = new Set(Object.keys(borderStyles));
var BORDER_STYLE_TOKEN_KEYS = Object.freeze(Object.keys(borderStyles).sort());

// src/tokens/border-widths.ts
var borderWidths = Object.freeze({
  xs: "0.5px",
  sm: "1px",
  md: "2px",
  lg: "4px",
  xl: "8px"
});
var BORDER_WIDTH_TOKEN_SET = new Set(Object.keys(borderWidths));
var BORDER_WIDTH_TOKEN_KEYS = Object.freeze(Object.keys(borderWidths).sort());

// src/tokens/colors.ts
var colors = {
  neutral: {
    50: "#f6f7f8",
    100: "#f1f1f4",
    200: "#e3e3e8",
    300: "#d4d5dd",
    400: "#b8b9c7",
    500: "#7e829a",
    600: "#4f5164",
    700: "#383a47",
    800: "#22232b",
    900: "#16171d"
  },
  green: {
    50: "#f1fdf4",
    100: "#dcfce8",
    200: "#bcf8d0",
    300: "#86f0ad",
    400: "#4bde80",
    500: "#22c55f",
    600: "#17a34a",
    700: "#106a32",
    800: "#032713",
    900: "#03180c"
  },
  yellow: {
    50: "#fefce8",
    100: "#fef9c3",
    200: "#fef18b",
    300: "#fde047",
    400: "#facc14",
    500: "#ebb30b",
    600: "#ca8a03",
    700: "#845208",
    800: "#432005",
    900: "#281304"
  },
  red: {
    50: "#fef3f2",
    100: "#ffe2e3",
    200: "#fecaca",
    300: "#fda5a5",
    400: "#f97171",
    500: "#ef4444",
    600: "#dc2627",
    700: "#9a1919",
    800: "#300c0c",
    900: "#1f0808"
  },
  blue: {
    50: "#f0f6ff",
    100: "#dbeafe",
    200: "#c0dbfe",
    300: "#a3cfff",
    400: "#61a6fb",
    500: "#3b83f6",
    600: "#2663eb",
    700: "#173da6",
    800: "#13204a",
    900: "#0b142e"
  }
};

// src/tokens/sizes.ts
var NUMERIC_SIZE_STEPS = [
  ...POSITIVE_SPACING_STEPS
];
var NAMED_SIZE_VALUES = {
  "3xs": "14rem",
  "2xs": "16rem",
  xs: "20rem",
  sm: "24rem",
  md: "28rem",
  lg: "32rem",
  xl: "36rem",
  "2xl": "42rem",
  "3xl": "48rem",
  "4xl": "56rem",
  "5xl": "64rem",
  "6xl": "72rem",
  full: "100%",
  min: "min-content",
  max: "max-content",
  fit: "fit-content",
  prose: "60ch",
  vw: "100vw",
  vh: "100vh",
  dvw: "100dvw",
  dvh: "100dvh",
  svw: "100svw",
  svh: "100svh",
  lvw: "100lvw",
  lvh: "100lvh",
  "1/2": "50%",
  "1/3": "33.333333%",
  "2/3": "66.666667%",
  "1/4": "25%",
  "3/4": "75%",
  "1/5": "20%",
  "2/5": "40%",
  "3/5": "60%",
  "4/5": "80%",
  "1/6": "16.666667%",
  "2/6": "33.333333%",
  "3/6": "50%",
  "4/6": "66.666667%",
  "5/6": "83.333333%",
  "1/12": "8.333333%",
  "2/12": "16.666667%",
  "3/12": "25%",
  "4/12": "33.333333%",
  "5/12": "41.666667%",
  "6/12": "50%",
  "7/12": "58.333333%",
  "8/12": "66.666667%",
  "9/12": "75%",
  "10/12": "83.333333%",
  "11/12": "91.666667%"
};
var NUMERIC_SIZE_VALUES = Object.fromEntries(NUMERIC_SIZE_STEPS.map((step) => [String(step), spacingValue(step)]));
var sizes = Object.freeze({
  ...NUMERIC_SIZE_VALUES,
  ...NAMED_SIZE_VALUES
});
var SIZE_TOKEN_SET = new Set(Object.keys(sizes));
var SIZE_TOKEN_KEYS = Object.freeze(Object.keys(sizes).sort());

// src/tokens/typography.ts
var TYPOGRAPHY_FONT_SIZE_KEYS = [
  "2xs",
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "5xl",
  "6xl",
  "7xl",
  "8xl",
  "9xl"
];
var TYPOGRAPHY_TEXT_STYLE_SCALE = {
  "2xs": { lineHeight: "shorter", letterSpacing: "normal" },
  xs: { lineHeight: "short", letterSpacing: "normal" },
  sm: { lineHeight: "short", letterSpacing: "normal" },
  md: { lineHeight: "moderate", letterSpacing: "normal" },
  lg: { lineHeight: "moderate", letterSpacing: "normal" },
  xl: { lineHeight: "moderate", letterSpacing: "normal" },
  "2xl": { lineHeight: "short", letterSpacing: "normal" },
  "3xl": { lineHeight: "shorter", letterSpacing: "normal" },
  "4xl": { lineHeight: "shorter", letterSpacing: "tight" },
  "5xl": { lineHeight: "shorter", letterSpacing: "tight" },
  "6xl": { lineHeight: "shorter", letterSpacing: "tight" },
  "7xl": { lineHeight: "shorter", letterSpacing: "tight" },
  "8xl": { lineHeight: "shorter", letterSpacing: "tight" },
  "9xl": { lineHeight: "shorter", letterSpacing: "tight" }
};
var defaultSansStack = '"Inter Variable", sans-serif';
var typography = {
  fontFamily: {
    body: defaultSansStack,
    heading: defaultSansStack,
    mono: '"JetBrains Mono Variable", monospace'
  },
  fontSize: {
    "2xs": 0.625,
    xs: 0.75,
    sm: 0.875,
    md: 1,
    lg: 1.125,
    xl: 1.25,
    "2xl": 1.5,
    "3xl": 1.875,
    "4xl": 2.25,
    "5xl": 3,
    "6xl": 3.75,
    "7xl": 4.5,
    "8xl": 6,
    "9xl": 8
  },
  lineHeight: {
    shorter: 1.25,
    short: 1.375,
    moderate: 1.5,
    tall: 1.625,
    taller: 2
  },
  fontWeight: {
    thin: 100,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em"
  }
};

// src/tokens/radii.ts
var RADIUS_ALIAS_REFERENCES = Object.freeze({
  l1: "{radii.xs}",
  l2: "{radii.sm}",
  l3: "{radii.md}"
});
var radii = {
  none: 0,
  "2xs": 1,
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  "2xl": 16,
  "3xl": 24,
  "4xl": 32,
  full: 9999
};
var RADIUS_TOKEN_SET = new Set([
  ...Object.keys(radii),
  ...Object.keys(RADIUS_ALIAS_REFERENCES)
]);
var RADIUS_TOKEN_KEYS = Object.freeze(Array.from(RADIUS_TOKEN_SET).sort());

// src/tokens/shadows.ts
var shadows = Object.freeze({
  none: "none",
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
});
var SHADOW_TOKEN_SET = new Set(Object.keys(shadows));
var SHADOW_TOKEN_KEYS = Object.freeze(Array.from(SHADOW_TOKEN_SET).sort());

// src/tokens/z-indices.ts
var zIndices = Object.freeze({
  hide: -1,
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1800,
  max: 2147483647
});
var Z_INDEX_TOKEN_SET = new Set(Object.keys(zIndices));
var Z_INDEX_TOKEN_KEYS = Object.freeze(Array.from(Z_INDEX_TOKEN_SET).sort());
// src/theme/component-token-defaults.ts
var ALERT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "alert.info.bg": "{colors.infoMuted}",
  "alert.info.border": "{colors.infoSubtle}",
  "alert.info.color": "{colors.infoEmphasized}",
  "alert.success.bg": "{colors.successMuted}",
  "alert.success.border": "{colors.successSubtle}",
  "alert.success.color": "{colors.successEmphasized}",
  "alert.warning.bg": "{colors.warningMuted}",
  "alert.warning.border": "{colors.warningSubtle}",
  "alert.warning.color": "{colors.warningEmphasized}",
  "alert.error.bg": "{colors.errorMuted}",
  "alert.error.border": "{colors.errorSubtle}",
  "alert.error.color": "{colors.errorEmphasized}"
});
var TOAST_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "toast.base.bg": "{colors.background}",
  "toast.base.surfaceBg": "{colors.background}",
  "toast.base.border": "{colors.borderSubtle}",
  "toast.base.color": "{colors.text}",
  "toast.base.descriptionColor": "{colors.textSubtle}",
  "toast.base.borderRadius": "{radii.xl}",
  "toast.base.borderWidth": "{borderWidths.sm}",
  "toast.base.borderStyle": "{borderStyles.solid}",
  "toast.base.minHeight": "{sizes.15}",
  "toast.base.minWidth": "0",
  "toast.base.maxWidth": "min(22.25rem, calc(100vw - 2rem))",
  "toast.base.paddingX": "{spacing.3}",
  "toast.base.paddingY": "{spacing.3}",
  "toast.base.gap": "{spacing.2}",
  "toast.base.contentGap": "0",
  "toast.base.descriptionPaddingLeft": "{spacing.6}",
  "toast.base.actionGap": "{spacing.2}",
  "toast.base.shadow": "{shadows.lg}",
  "toast.icon.color": "{colors.text}",
  "toast.default.bg": "{colors.background}",
  "toast.default.border": "{colors.borderSubtle}",
  "toast.default.icon": "{colors.text}",
  "toast.success.bg": "{colors.successMuted}",
  "toast.success.border": "{colors.success}",
  "toast.success.icon": "{colors.successEmphasized}",
  "toast.info.bg": "{colors.infoMuted}",
  "toast.info.border": "{colors.info}",
  "toast.info.icon": "{colors.infoEmphasized}",
  "toast.warning.bg": "{colors.warningMuted}",
  "toast.warning.border": "{colors.warning}",
  "toast.warning.icon": "{colors.warningEmphasized}",
  "toast.error.bg": "{colors.errorMuted}",
  "toast.error.border": "{colors.error}",
  "toast.error.icon": "{colors.errorEmphasized}",
  "toast.loading.bg": "{colors.background}",
  "toast.loading.border": "{colors.borderSubtle}",
  "toast.loading.icon": "{colors.text}",
  "toast.action.color": "{colors.text}",
  "toast.action.hoverBg": "{colors.surface3Alpha32}",
  "toast.close.color": "{colors.text}",
  "toast.close.hoverBg": "{colors.surface3Alpha32}",
  "toaster.offset": "{spacing.4}",
  "toaster.width": "min(22.25rem, calc(100vw - 2rem))",
  "toaster.maxWidth": "calc(100vw - 2rem)",
  "toaster.gap": "{spacing.2}",
  "toaster.stack.collapsedOffset": "{spacing.2}",
  "toaster.stack.scaleStep": "0.04",
  "toaster.stack.transition": "400ms ease",
  "toaster.zIndex": "{zIndices.tooltip}"
});
var BUTTON_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "button.primary.bg": "{colors.accent}",
  "button.primary.color": "{colors.textInverse}",
  "button.primary.hover.bg": "{colors.accentSubtle}",
  "button.primary.focus.outline": "{colors.borderFocus}",
  "button.secondary.bg": "{colors.neutralDefault}",
  "button.secondary.color": "{colors.text}",
  "button.secondary.hover.bg": "{colors.neutralSubtle}",
  "button.secondary.focus.outline": "{colors.borderFocus}",
  "button.ghost.bg": "transparent",
  "button.ghost.color": "{colors.text}",
  "button.ghost.hover.bg": "{colors.surface3}",
  "button.ghost.focus.outline": "{colors.borderFocus}",
  "button.outline.bg": "{colors.surface1}",
  "button.outline.color": "{colors.text}",
  "button.outline.border": "{colors.border}",
  "button.outline.hover.bg": "{colors.surface2}",
  "button.outline.focus.outline": "{colors.borderFocus}",
  "button.destructive.bg": "{colors.error}",
  "button.destructive.color": "{colors.textInverse}",
  "button.destructive.hover.bg": "{colors.errorSubtle}",
  "button.destructive.focus.outline": "{colors.error}",
  "button.ghostDestructive.bg": "transparent",
  "button.ghostDestructive.color": "{colors.error}",
  "button.ghostDestructive.hover.bg": "{colors.errorMuted}",
  "button.ghostDestructive.focus.outline": "{colors.errorEmphasized}",
  "button.focus.outlineWidth": "{borderWidths.md}",
  "button.focus.outlineStyle": "{borderStyles.solid}",
  "button.focus.outlineOffset": "{spacing.0.5}",
  "button.base.borderRadius": "{radii.lg}",
  "button.base.fontFamily": "{typography.fontFamily.body}",
  "button.base.fontWeight": "{typography.fontWeight.medium}",
  "button.base.textTransform": "none",
  "button.base.transition": "all 150ms ease-in-out",
  "button.disabled.true.opacity": "0.5",
  "button.disabled.true.cursor": "not-allowed",
  "button.disabled.true.pointerEvents": "none",
  "button.size.sm.paddingLeft": "{spacing.2}",
  "button.size.sm.paddingRight": "{spacing.2}",
  "button.size.sm.height": "{sizes.6}",
  "button.size.sm.borderRadius": "{radii.md}",
  "button.size.sm.fontSize": "{typography.fontSize.sm}",
  "button.size.sm.gap": "{spacing.0.5}",
  "button.size.sm.iconPaddingLeft": "{spacing.1}",
  "button.size.sm.iconPaddingRight": "{spacing.1}",
  "button.size.sm.iconPaddingBoth": "{spacing.1}",
  "button.size.md.paddingLeft": "{spacing.3}",
  "button.size.md.paddingRight": "{spacing.3}",
  "button.size.md.height": "{sizes.8}",
  "button.size.md.borderRadius": "{radii.lg}",
  "button.size.md.fontSize": "{typography.fontSize.sm}",
  "button.size.md.gap": "{spacing.1}",
  "button.size.md.iconPaddingLeft": "{spacing.2}",
  "button.size.md.iconPaddingRight": "{spacing.2}",
  "button.size.md.iconPaddingBoth": "{spacing.2}",
  "button.size.lg.paddingLeft": "{spacing.4}",
  "button.size.lg.paddingRight": "{spacing.4}",
  "button.size.lg.height": "{sizes.10}",
  "button.size.lg.borderRadius": "{radii.lg}",
  "button.size.lg.fontSize": "{typography.fontSize.md}",
  "button.size.lg.gap": "{spacing.1}",
  "button.size.lg.iconPaddingLeft": "{spacing.3}",
  "button.size.lg.iconPaddingRight": "{spacing.3}",
  "button.size.lg.iconPaddingBoth": "{spacing.3}"
});
var NAV_TREE_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "navTree.bg": "transparent",
  "navTree.color": "{colors.text}",
  "navTree.root.bg": "{navTree.bg}",
  "navTree.root.color": "{navTree.color}",
  "navTree.section.paddingX": "{spacing.2}",
  "navTree.section.header.height": "{sizes.10}",
  "navTree.section.header.color": "{colors.textDisabled}",
  "navTree.section.header.fontFamily": "{typography.fontFamily.mono}",
  "navTree.section.header.fontSize": "{typography.fontSize.sm}",
  "navTree.section.header.fontWeight": "{typography.fontWeight.normal}",
  "navTree.section.header.lineHeight": "{typography.lineHeight.short}",
  "navTree.section.header.textTransform": "uppercase",
  "navTree.section.header.gap": "{spacing.2}",
  "navTree.section.header.paddingX": "{navTree.section.paddingX}",
  "navTree.section.title.color": "inherit",
  "navTree.section.title.fontFamily": "inherit",
  "navTree.section.title.fontSize": "inherit",
  "navTree.section.title.fontWeight": "inherit",
  "navTree.section.title.lineHeight": "inherit",
  "navTree.section.title.textTransform": "inherit",
  "navTree.section.end.color": "inherit",
  "navTree.section.end.minWidth": "{sizes.0}",
  "navTree.section.items.gap": "{spacing.0}",
  "navTree.item.bg": "transparent",
  "navTree.item.color": "{colors.text}",
  "navTree.item.hover.bg": "{colors.surface3Alpha32}",
  "navTree.item.selected.bg": "{colors.surface2}",
  "navTree.item.selected.border": "{colors.borderStrong}",
  "navTree.item.selected.color": "{colors.text}",
  "navTree.item.selected.fontWeight": "{typography.fontWeight.normal}",
  "navTree.item.descendantSelected.bg": "{colors.surface2Alpha32}",
  "navTree.item.base.bg": "{navTree.item.bg}",
  "navTree.item.base.color": "{navTree.item.color}",
  "navTree.item.base.fontFamily": "{navTree.item.fontFamily}",
  "navTree.item.base.fontSize": "{navTree.item.fontSize}",
  "navTree.item.base.fontWeight": "{navTree.item.fontWeight}",
  "navTree.item.base.height": "{navTree.item.height}",
  "navTree.item.base.paddingRight": "{navTree.item.pr}",
  "navTree.item.base.paddingLevel1": "{navTree.item.l1.pad}",
  "navTree.item.base.paddingLevel1WithIcon": "{navTree.item.l1.iconPad}",
  "navTree.item.base.nestedPadding": "{navTree.item.nest.pad}",
  "navTree.item.base.nestedIconPadding": "{navTree.item.nest.iconPad}",
  "navTree.item.base.nestedStep": "{navTree.item.nest.step}",
  "navTree.item.base.borderRadius": "{navTree.item.borderRadius}",
  "navTree.item.base.transition": "background-color 150ms ease-in-out, outline-color 150ms ease-in-out",
  "navTree.item.focus.bg": "{navTree.item.hover.bg}",
  "navTree.item.selected.outlineWidth": "{borderWidths.sm}",
  "navTree.item.selected.outlineStyle": "{borderStyles.solid}",
  "navTree.item.selected.outlineOffset": "-1px",
  "navTree.item.group.descendantBg": "{navTree.item.descendantSelected.bg}",
  "navTree.item.group.outlineWidth": "{borderWidths.sm}",
  "navTree.item.group.outlineStyle": "{borderStyles.solid}",
  "navTree.item.group.outlineColor": "{navTree.item.selected.border}",
  "navTree.item.group.borderWidth": "{borderWidths.sm}",
  "navTree.item.group.borderStyle": "{borderStyles.solid}",
  "navTree.item.group.borderColor": "{colors.borderStrong}",
  "navTree.item.disabled.opacity": "{navTree.disabled.opacity}",
  "navTree.item.disabled.cursor": "not-allowed",
  "navTree.item.dragging.opacity": "0.56",
  "navTree.item.dragging.cursor": "grabbing",
  "navTree.item.dragSource.cursor": "grab",
  "navTree.item.droppable.cursor": "move",
  "navTree.item.dropActive.bg": "{colors.surface3Alpha32}",
  "navTree.item.dropActive.outlineWidth": "{borderWidths.sm}",
  "navTree.item.dropActive.outlineStyle": "{borderStyles.solid}",
  "navTree.item.dropActive.outlineColor": "{colors.borderFocus}",
  "navTree.item.dropActive.outlineOffset": "-1px",
  "navTree.rootDropZone.hitAreaHeight": "{sizes.6}",
  "navTree.rail.color": "{colors.border}",
  "navTree.rail.start": "{spacing.4}",
  "navTree.rail.step": "{spacing.5.5}",
  "navTree.rail.width": "{sizes.0}",
  "navTree.rail.borderWidth": "{borderWidths.sm}",
  "navTree.rail.borderStyle": "{borderStyles.solid}",
  "navTree.toggle.bg": "{colors.surface3}",
  "navTree.toggle.color": "{colors.text}",
  "navTree.toggle.hover.bg": "{colors.surface2}",
  "navTree.toggle.focusRing": "{colors.borderFocus}",
  "navTree.toggle.boxSize": "{sizes.4}",
  "navTree.toggle.borderRadius": "{radii.sm}",
  "navTree.toggle.base.bg": "{navTree.toggle.bg}",
  "navTree.toggle.base.color": "{navTree.toggle.color}",
  "navTree.toggle.base.boxSize": "{navTree.toggle.boxSize}",
  "navTree.toggle.base.borderRadius": "{navTree.toggle.borderRadius}",
  "navTree.toggle.focus.bg": "{navTree.toggle.hover.bg}",
  "navTree.gap.toggle": "{spacing.0.5}",
  "navTree.gap.icon": "{spacing.0.5}",
  "navTree.gap.label": "{spacing.1.5}",
  "navTree.gap.end": "{spacing.1.5}",
  "navTree.control.gap": "{navTree.gap.end}",
  "navTree.control.cursor": "pointer",
  "navTree.control.staticCursor": "default",
  "navTree.icon.leading.boxSize": "{sizes.6}",
  "navTree.icon.trailing.boxSize": "{sizes.4}",
  "navTree.icon.trailing.color": "{colors.textDisabled}",
  "navTree.item.borderRadius": "{radii.lg}",
  "navTree.item.fontFamily": "{typography.fontFamily.body}",
  "navTree.item.fontSize": "{typography.fontSize.sm}",
  "navTree.item.fontWeight": "{typography.fontWeight.normal}",
  "navTree.item.height": "{sizes.8}",
  "navTree.item.pr": "{spacing.2}",
  "navTree.item.l1.pad": "{spacing.2}",
  "navTree.item.l1.iconPad": "{spacing.1}",
  "navTree.item.nest.pad": "{spacing.7.5}",
  "navTree.item.nest.iconPad": "{spacing.6.5}",
  "navTree.item.nest.step": "{spacing.5.5}",
  "navTree.disabled.opacity": "0.5",
  "navTree.size.sm.item.height": "{sizes.7}",
  "navTree.size.sm.item.fontSize": "{typography.fontSize.xs}",
  "navTree.size.sm.item.borderRadius": "{radii.md}",
  "navTree.size.sm.item.paddingRight": "{spacing.1.5}",
  "navTree.size.sm.item.paddingLevel1": "{spacing.1.5}",
  "navTree.size.sm.item.paddingLevel1WithIcon": "{spacing.0.5}",
  "navTree.size.sm.item.nestedPadding": "{spacing.6}",
  "navTree.size.sm.item.nestedIconPadding": "{spacing.5}",
  "navTree.size.sm.item.nestedStep": "{spacing.4.5}",
  "navTree.size.sm.gap.toggle": "{spacing.0.5}",
  "navTree.size.sm.gap.icon": "{spacing.0.5}",
  "navTree.size.sm.gap.label": "{spacing.1}",
  "navTree.size.sm.gap.end": "{spacing.1}",
  "navTree.size.sm.icon.leading.boxSize": "{sizes.5}",
  "navTree.size.sm.icon.trailing.boxSize": "{sizes.3.5}",
  "navTree.size.sm.toggle.boxSize": "{sizes.3.5}",
  "navTree.size.md.item.height": "{navTree.item.height}",
  "navTree.size.md.item.fontSize": "{navTree.item.fontSize}",
  "navTree.size.md.item.borderRadius": "{navTree.item.borderRadius}",
  "navTree.size.md.item.paddingRight": "{navTree.item.pr}",
  "navTree.size.md.item.paddingLevel1": "{navTree.item.l1.pad}",
  "navTree.size.md.item.paddingLevel1WithIcon": "{navTree.item.l1.iconPad}",
  "navTree.size.md.item.nestedPadding": "{navTree.item.nest.pad}",
  "navTree.size.md.item.nestedIconPadding": "{navTree.item.nest.iconPad}",
  "navTree.size.md.item.nestedStep": "{navTree.item.nest.step}",
  "navTree.size.md.gap.toggle": "{navTree.gap.toggle}",
  "navTree.size.md.gap.icon": "{navTree.gap.icon}",
  "navTree.size.md.gap.label": "{navTree.gap.label}",
  "navTree.size.md.gap.end": "{navTree.gap.end}",
  "navTree.size.md.icon.leading.boxSize": "{navTree.icon.leading.boxSize}",
  "navTree.size.md.icon.trailing.boxSize": "{navTree.icon.trailing.boxSize}",
  "navTree.size.md.toggle.boxSize": "{navTree.toggle.boxSize}",
  "navTree.size.lg.item.height": "{sizes.10}",
  "navTree.size.lg.item.fontSize": "{typography.fontSize.sm}",
  "navTree.size.lg.item.borderRadius": "{radii.lg}",
  "navTree.size.lg.item.paddingRight": "{spacing.2.5}",
  "navTree.size.lg.item.paddingLevel1": "{spacing.2.5}",
  "navTree.size.lg.item.paddingLevel1WithIcon": "{spacing.1.5}",
  "navTree.size.lg.item.nestedPadding": "{spacing.8.5}",
  "navTree.size.lg.item.nestedIconPadding": "{spacing.7.5}",
  "navTree.size.lg.item.nestedStep": "{spacing.6}",
  "navTree.size.lg.gap.toggle": "{spacing.0.5}",
  "navTree.size.lg.gap.icon": "{spacing.0.5}",
  "navTree.size.lg.gap.label": "{spacing.2}",
  "navTree.size.lg.gap.end": "{spacing.2}",
  "navTree.size.lg.icon.leading.boxSize": "{sizes.6}",
  "navTree.size.lg.icon.trailing.boxSize": "{sizes.4}",
  "navTree.size.lg.toggle.boxSize": "{sizes.4}"
});
var BREADCRUMB_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "breadcrumb.color": "{colors.textSubtle}",
  "breadcrumb.current.color": "{colors.text}",
  "breadcrumb.current.fontWeight": "{typography.fontWeight.bold}",
  "breadcrumb.link.color": "{colors.textSubtle}",
  "breadcrumb.link.hover.color": "{colors.text}",
  "breadcrumb.link.focus.outlineColor": "{colors.borderFocus}",
  "breadcrumb.link.focus.outlineOffset": "{spacing.0.5}",
  "breadcrumb.link.focus.outlineStyle": "{borderStyles.solid}",
  "breadcrumb.link.focus.outlineWidth": "{borderWidths.md}",
  "breadcrumb.separator.color": "{colors.textDisabled}",
  "breadcrumb.separator.opacity": "0.72",
  "breadcrumb.ellipsis.color": "{colors.textDisabled}",
  "breadcrumb.size.sm.fontSize": "{typography.fontSize.xs}",
  "breadcrumb.size.sm.lineHeight": "{typography.lineHeight.short}",
  "breadcrumb.size.sm.letterSpacing": "{typography.letterSpacing.normal}",
  "breadcrumb.size.sm.gap": "{spacing.1}",
  "breadcrumb.size.sm.iconSize": "{sizes.3}",
  "breadcrumb.link.sm.gap": "{spacing.0.5}",
  "breadcrumb.link.sm.radius": "{radii.xs}",
  "breadcrumb.size.md.fontSize": "{typography.fontSize.sm}",
  "breadcrumb.size.md.lineHeight": "{typography.lineHeight.moderate}",
  "breadcrumb.size.md.letterSpacing": "{typography.letterSpacing.normal}",
  "breadcrumb.size.md.gap": "{spacing.1.5}",
  "breadcrumb.size.md.iconSize": "{sizes.4}",
  "breadcrumb.link.md.gap": "{spacing.1}",
  "breadcrumb.link.md.radius": "{radii.sm}",
  "breadcrumb.size.lg.fontSize": "{typography.fontSize.md}",
  "breadcrumb.size.lg.lineHeight": "{typography.lineHeight.moderate}",
  "breadcrumb.size.lg.letterSpacing": "{typography.letterSpacing.normal}",
  "breadcrumb.size.lg.gap": "{spacing.2}",
  "breadcrumb.size.lg.iconSize": "{sizes.4}",
  "breadcrumb.link.lg.gap": "{spacing.1.5}",
  "breadcrumb.link.lg.radius": "{radii.md}"
});
var INPUT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "input.base.borderRadius": "{radii.md}",
  "input.base.fontFamily": "{typography.fontFamily.body}",
  "input.base.transition": "all 150ms ease-in-out",
  "input.clear.color": "{button.ghost.color}",
  "input.clear.bg": "{button.ghost.bg}",
  "input.clear.hover.color": "{button.ghost.color}",
  "input.clear.hover.bg": "{button.ghost.hover.bg}",
  "input.clear.focus.outlineColor": "{button.ghost.focus.outline}",
  "input.clear.focus.outlineOffset": "{spacing.0}",
  "input.clear.focus.outlineStyle": "{borderStyles.solid}",
  "input.clear.focus.outlineWidth": "{borderWidths.md}",
  "input.clear.size.sm.boxSize": "{sizes.4}",
  "input.clear.size.sm.borderRadius": "{radii.sm}",
  "input.clear.size.md.boxSize": "{sizes.5}",
  "input.clear.size.md.borderRadius": "{radii.md}",
  "input.clear.size.lg.boxSize": "{sizes.5}",
  "input.clear.size.lg.borderRadius": "{radii.md}",
  "textarea.clear.size.sm.top": "{spacing.1}",
  "textarea.clear.size.md.top": "{spacing.2}",
  "textarea.clear.size.lg.top": "{spacing.2.5}",
  "textarea.actions.size.sm.bottom": "{spacing.1}",
  "textarea.actions.size.md.bottom": "{spacing.1.5}",
  "textarea.actions.size.lg.bottom": "{spacing.2.5}",
  "textarea.actions.size.sm.gap": "{spacing.1}",
  "textarea.actions.size.md.gap": "{spacing.1}",
  "textarea.actions.size.lg.gap": "{spacing.1}",
  "textarea.actions.size.sm.height": "{sizes.6}",
  "textarea.actions.size.md.height": "{sizes.8}",
  "textarea.actions.size.lg.height": "{sizes.8}",
  "input.placeholder.color": "{colors.textSubtle/50}",
  "input.outline.bg": "transparent",
  "input.outline.border": "{colors.borderSubtle}",
  "input.outline.color": "{colors.text}",
  "input.outline.hover.bg": "{colors.surface3Alpha32}",
  "input.filled.bg": "{colors.surface3Alpha32}",
  "input.filled.border": "{colors.borderSubtle}",
  "input.filled.color": "{colors.text}",
  "input.filled.hover.bg": "{colors.surface2Alpha32}",
  "input.flushed.bg": "transparent",
  "input.flushed.border": "{colors.borderSubtle}",
  "input.flushed.color": "{colors.text}",
  "input.flushed.hover.borderColor": "{colors.text}",
  "input.ghost.bg": "transparent",
  "input.ghost.border": "transparent",
  "input.ghost.color": "{colors.text}",
  "input.ghost.hover.bg": "{colors.surface3Alpha32}",
  "input.focus.outlineColor": "{colors.borderFocus}",
  "input.focus.outlineOffset": "{spacing.0.5}",
  "input.focus.outlineStyle": "{borderStyles.solid}",
  "input.focus.outlineWidth": "{borderWidths.md}",
  "input.size.sm.paddingLeft": "{spacing.1.5}",
  "input.size.sm.paddingRight": "{spacing.1.5}",
  "input.size.sm.paddingX": "{spacing.2}",
  "input.size.sm.paddingY": "{spacing.1}",
  "input.size.sm.fontSize": "{typography.fontSize.sm}",
  "input.size.sm.height": "{sizes.6}",
  "input.size.sm.borderRadius": "{radii.md}",
  "input.size.md.paddingLeft": "{spacing.2}",
  "input.size.md.paddingRight": "{spacing.2}",
  "input.size.md.paddingX": "{spacing.2}",
  "input.size.md.paddingY": "{spacing.1.5}",
  "input.size.md.fontSize": "{typography.fontSize.sm}",
  "input.size.md.height": "{sizes.8}",
  "input.size.md.borderRadius": "{radii.lg}",
  "input.size.lg.paddingX": "{spacing.3}",
  "input.size.lg.paddingLeft": "{spacing.3}",
  "input.size.lg.paddingRight": "{spacing.3}",
  "input.size.lg.paddingY": "{spacing.2.5}",
  "input.size.lg.fontSize": "{typography.fontSize.md}",
  "input.size.lg.height": "{sizes.10}",
  "input.size.lg.borderRadius": "{radii.lg}",
  "textarea.size.sm.minHeight": "{sizes.25}",
  "textarea.size.md.minHeight": "{sizes.37.5}",
  "textarea.size.lg.minHeight": "{sizes.50}",
  "input.disabled.true.opacity": "0.5",
  "input.disabled.true.cursor": "not-allowed",
  "input.disabled.true.pointerEvents": "auto",
  "input.invalid.borderColor": "{colors.errorEmphasized}",
  "input.invalid.filled.bg": "{colors.surface2}",
  "input.selectChevron.bg": "{colors.surface3}",
  "input.selectChevron.color": "{colors.text}",
  "input.selectChevron.borderRadius": "{radii.sm}",
  "input.selectChevron.boxSize": "{sizes.4}"
});
var INPUT_GROUP_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "input-group.content.paddingX": "{spacing.1}",
  "input-group.start.color": "{colors.textSubtle}",
  "input-group.end.color": "{colors.textSubtle}",
  "input-group.size.sm.gap": "{spacing.1}",
  "input-group.size.sm.startPaddingLeft": "{spacing.1.5 }",
  "input-group.size.sm.endPaddingRight": "{spacing.1.5}",
  "input-group.size.sm.startElementMinWidth": "{sizes.4}",
  "input-group.size.sm.endElementMinWidth": "{sizes.4}",
  "input-group.size.md.gap": "{spacing.1}",
  "input-group.size.md.startPaddingLeft": "{spacing.2}",
  "input-group.size.md.endPaddingRight": "{spacing.2}",
  "input-group.size.md.startElementMinWidth": "{sizes.6}",
  "input-group.size.md.endElementMinWidth": "{sizes.6}",
  "input-group.size.lg.gap": "{spacing.1}",
  "input-group.size.lg.startPaddingLeft": "{spacing.3}",
  "input-group.size.lg.endPaddingRight": "{spacing.3}",
  "input-group.size.lg.startElementMinWidth": "{sizes.6}",
  "input-group.size.lg.endElementMinWidth": "{sizes.6}"
});
var FLYOUT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "flyout.bg": "{colors.surface1}",
  "flyout.border": "{colors.borderSubtle}",
  "flyout.borderWidth": "{borderWidths.sm}",
  "flyout.borderStyle": "{borderStyles.solid}",
  "flyout.borderRadius": "10px",
  "flyout.shadow": "0 2px 8px 0 rgba(0, 0, 0, 0.24), 0 0 16px 0 rgba(0, 0, 0, 0.08)",
  "flyout.maxHeight": "18.75rem",
  "flyout.size.sm.borderRadius": "{radii.lg}",
  "flyout.size.sm.paddingX": "{spacing.0.5}",
  "flyout.size.sm.paddingY": "{spacing.0.5}",
  "flyout.size.md.paddingX": "{spacing.1}",
  "flyout.size.md.paddingY": "{spacing.1}",
  "flyout.size.lg.paddingX": "{spacing.1}",
  "flyout.size.lg.paddingY": "{spacing.1}",
  "flyout-footer.bg": "{colors.surface2}",
  "flyout-footer-action.bg": "transparent",
  "flyout-footer-action.hover.bg": "{colors.surface3Alpha32}",
  "flyout-footer-action.size.sm.paddingX": "{spacing.1.5}",
  "flyout-footer-action.size.md.paddingX": "{spacing.2}",
  "flyout-footer-action.size.lg.paddingX": "{spacing.3}",
  "flyout-footer-action.size.md.noIconPaddingLeft": "{spacing.3}",
  "flyout-footer-action.size.lg.noIconPaddingLeft": "{spacing.4}",
  "flyout-group-label.section.fontFamily": "{typography.fontFamily.body}",
  "flyout-group-label.section.fontSize": "{typography.fontSize.xs}",
  "flyout-group-label.section.fontWeight": "{typography.fontWeight.bold}",
  "flyout-group-label.section.lineHeight": "var(--smth-text-styles-body-sm-line-height)",
  "flyout-group-label.section.textTransform": "uppercase",
  "flyout-item.bg": "transparent",
  "flyout-item.color": "{colors.text}",
  "flyout-item.suffix.color": "{colors.textSubtle}",
  "flyout-item.hover.bg": "{colors.surface3Alpha32}",
  "flyout-item.pressed.bg": "{colors.surface3Alpha32}",
  "flyout-item.pressed.border": "{colors.borderFocus}",
  "flyout-item.disabled.opacity": "0.5",
  "flyout-item.check.color": "{colors.text}",
  "flyout-item.icon.color": "{colors.text}",
  "flyout-item.gap": "{spacing.1}",
  "flyout-item.checkIcon.gap": "{spacing.0}",
  "flyout-item.size.sm.height": "{sizes.6}",
  "flyout-item.size.sm.paddingX": "{spacing.1}",
  "flyout-item.size.sm.paddingY": "{spacing.1}",
  "flyout-item.size.sm.borderRadius": "{radii.sm}",
  "flyout-item.size.sm.fontSize": "{typography.fontSize.sm}",
  "flyout-item.size.sm.suffixFontSize": "{typography.fontSize.xs}",
  "flyout-item.size.sm.checkBoxSize": "{sizes.4}",
  "flyout-item.size.sm.iconBoxSize": "{sizes.4}",
  "flyout-item.size.md.height": "{sizes.8}",
  "flyout-item.size.md.paddingX": "{spacing.1}",
  "flyout-item.size.md.paddingY": "{spacing.1}",
  "flyout-item.size.md.borderRadius": "{radii.md}",
  "flyout-item.size.md.fontSize": "{typography.fontSize.sm}",
  "flyout-item.size.md.suffixFontSize": "{typography.fontSize.xs}",
  "flyout-item.size.md.checkBoxSize": "{sizes.6}",
  "flyout-item.size.md.iconBoxSize": "{sizes.6}",
  "flyout-item.size.lg.height": "{sizes.10}",
  "flyout-item.size.lg.paddingX": "{spacing.2}",
  "flyout-item.size.lg.paddingY": "{spacing.1}",
  "flyout-item.size.lg.borderRadius": "{radii.lg}",
  "flyout-item.size.lg.fontSize": "{typography.fontSize.md}",
  "flyout-item.size.lg.suffixFontSize": "{typography.fontSize.xs}",
  "flyout-item.size.lg.checkBoxSize": "{sizes.6}",
  "flyout-item.size.lg.iconBoxSize": "{sizes.6}"
});
var DROPDOWN_MENU_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "dropdown-menu.trigger.bg": "{button.ghost.bg}",
  "dropdown-menu.trigger.color": "{button.ghost.color}",
  "dropdown-menu.trigger.hoverBg": "{button.ghost.hover.bg}",
  "dropdown-menu.trigger.focusOutline": "{button.ghost.focus.outline}",
  "dropdown-menu.content.bg": "{flyout.bg}",
  "dropdown-menu.content.border": "{flyout.border}",
  "dropdown-menu.content.borderWidth": "{flyout.borderWidth}",
  "dropdown-menu.content.borderStyle": "{flyout.borderStyle}",
  "dropdown-menu.content.borderRadius": "{flyout.borderRadius}",
  "dropdown-menu.content.shadow": "{flyout.shadow}",
  "dropdown-menu.content.minWidth": "{sizes.32}",
  "dropdown-menu.content.maxWidth": "calc(100vw - 2rem)",
  "dropdown-menu.content.maxHeight": "{flyout.maxHeight}",
  "dropdown-menu.content.padding": "{spacing.1}",
  "dropdown-menu.content.zIndex": "{zIndices.tooltip}",
  "dropdown-menu.content.triggerOffset.sm.default": "0",
  "dropdown-menu.content.triggerOffset.sm.iconLeft": "{spacing.1}",
  "dropdown-menu.content.triggerOffset.sm.onlyIcon": "{spacing.1}",
  "dropdown-menu.content.triggerOffset.md.default": "{spacing.0.5}",
  "dropdown-menu.content.triggerOffset.md.iconLeft": "{spacing.0.5}",
  "dropdown-menu.content.triggerOffset.md.onlyIcon": "{spacing.1.5}",
  "dropdown-menu.content.triggerOffset.lg.default": "0",
  "dropdown-menu.content.triggerOffset.lg.iconLeft": "0",
  "dropdown-menu.content.triggerOffset.lg.onlyIcon": "{spacing.1}",
  "dropdown-menu.submenu.overlap.sm": "{spacing.0.5}",
  "dropdown-menu.submenu.overlap.md": "{spacing.1}",
  "dropdown-menu.submenu.overlap.lg": "{spacing.1.5}",
  "dropdown-menu.submenu.offsetY": "-0.25rem",
  "dropdown-menu.submenu.icon.bg": "{input.selectChevron.bg}",
  "dropdown-menu.submenu.icon.color": "{input.selectChevron.color}",
  "dropdown-menu.submenu.icon.borderRadius": "{input.selectChevron.borderRadius}",
  "dropdown-menu.submenu.icon.boxSize": "{input.selectChevron.boxSize}",
  "dropdown-menu.size.sm.paddingX": "{flyout.size.sm.paddingX}",
  "dropdown-menu.size.sm.paddingY": "{flyout.size.sm.paddingY}",
  "dropdown-menu.size.md.paddingX": "{flyout.size.md.paddingX}",
  "dropdown-menu.size.md.paddingY": "{flyout.size.md.paddingY}",
  "dropdown-menu.size.lg.paddingX": "{flyout.size.lg.paddingX}",
  "dropdown-menu.size.lg.paddingY": "{flyout.size.lg.paddingY}",
  "dropdown-menu.item.bg": "transparent",
  "dropdown-menu.item.color": "{colors.text}",
  "dropdown-menu.item.hoverBg": "{colors.surface3Alpha32}",
  "dropdown-menu.item.destructiveColor": "{colors.error}",
  "dropdown-menu.item.destructiveHoverBg": "{colors.errorMuted}",
  "dropdown-menu.item.disabledOpacity": "0.5",
  "dropdown-menu.item.indicatorColor": "{colors.text}",
  "dropdown-menu.item.shortcutColor": "{colors.textSubtle}",
  "dropdown-menu.item.endColor": "{colors.textSubtle}",
  "dropdown-menu.item.gap": "{spacing.0}",
  "dropdown-menu.item.size.sm.height": "{sizes.6}",
  "dropdown-menu.item.size.sm.paddingX": "{spacing.1}",
  "dropdown-menu.item.size.sm.paddingY": "{spacing.1}",
  "dropdown-menu.item.size.sm.gap": "{spacing.1}",
  "dropdown-menu.item.size.sm.borderRadius": "{radii.sm}",
  "dropdown-menu.item.size.sm.fontSize": "{typography.fontSize.sm}",
  "dropdown-menu.item.size.sm.indicatorSize": "{sizes.4}",
  "dropdown-menu.item.size.md.height": "{sizes.8}",
  "dropdown-menu.item.size.md.paddingX": "{spacing.1.5}",
  "dropdown-menu.item.size.md.paddingY": "{spacing.1}",
  "dropdown-menu.item.size.md.borderRadius": "{radii.md}",
  "dropdown-menu.item.size.md.fontSize": "{typography.fontSize.sm}",
  "dropdown-menu.item.size.md.indicatorSize": "{sizes.6}",
  "dropdown-menu.item.size.md.labelPaddingL": "{spacing.1}",
  "dropdown-menu.item.size.lg.height": "{sizes.10}",
  "dropdown-menu.item.size.lg.paddingX": "{spacing.2}",
  "dropdown-menu.item.size.lg.paddingY": "{spacing.1}",
  "dropdown-menu.item.size.lg.borderRadius": "{radii.lg}",
  "dropdown-menu.item.size.lg.fontSize": "{typography.fontSize.md}",
  "dropdown-menu.item.size.lg.indicatorSize": "{sizes.6}",
  "dropdown-menu.item.size.lg.labelPaddingL": "{spacing.1}",
  "dropdown-menu.label.color": "{colors.textSubtle}",
  "dropdown-menu.label.fontSize": "{typography.fontSize.xs}",
  "dropdown-menu.label.fontWeight": "{typography.fontWeight.medium}",
  "dropdown-menu.label.size.sm.paddingX": "{spacing.1}",
  "dropdown-menu.label.size.md.paddingX": "{spacing.2}",
  "dropdown-menu.label.size.lg.paddingX": "{spacing.3}",
  "dropdown-menu.label.paddingY": "{spacing.1}",
  "dropdown-menu.separator.bg": "{colors.borderSubtle}",
  "dropdown-menu.separator.marginY": "{spacing.1}",
  "dropdown-menu.shortcut.fontSize": "{typography.fontSize.xs}"
});
var MODAL_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "modal.backdrop.bg": "{colors.neutral.900/75}",
  "modal.backdrop.backdropFilter": "none",
  "modal.positioner.padding": "{spacing.4}",
  "modal.positioner.zIndex": "{zIndices.modal}",
  "modal.content.bg": "{colors.surface1}",
  "modal.content.border": "{colors.borderSubtle}",
  "modal.content.borderWidth": "{borderWidths.sm}",
  "modal.content.borderStyle": "{borderStyles.solid}",
  "modal.content.borderRadius": "{radii.xl}",
  "modal.content.shadow": "{shadows.2xl}",
  "modal.content.color": "{colors.text}",
  "modal.content.mobile.width": "100vw",
  "modal.content.mobile.height": "100dvh",
  "modal.content.mobile.maxHeight": "100dvh",
  "modal.content.mobile.borderRadius": "{radii.none}",
  "modal.content.size.sm.maxHeight": "48dvh",
  "modal.content.size.sm.minHeight": "auto",
  "modal.content.size.sm.width.md": "48%",
  "modal.content.size.sm.width.lg": "32%",
  "modal.content.size.sm.width.2xl": "24%",
  "modal.content.size.md.maxHeight": "72dvh",
  "modal.content.size.md.minHeight": "25rem",
  "modal.content.size.md.width.md": "60%",
  "modal.content.size.md.width.lg": "42%",
  "modal.content.size.md.width.2xl": "36%",
  "modal.content.size.lg.maxHeight": "84dvh",
  "modal.content.size.lg.minHeight": "31.25rem",
  "modal.content.size.lg.width.md": "84%",
  "modal.content.size.lg.width.lg": "60%",
  "modal.content.size.lg.width.2xl": "48%",
  "modal.header.paddingX": "{spacing.4}",
  "modal.header.paddingTop": "{spacing.4}",
  "modal.header.paddingBottom": "{spacing.2}",
  "modal.header.gap": "{spacing.1}",
  "modal.title.color": "{colors.text}",
  "modal.title.fontFamily": "{typography.fontFamily.body}",
  "modal.title.fontSize": "{typography.fontSize.md}",
  "modal.title.fontWeight": "{typography.fontWeight.semibold}",
  "modal.title.lineHeight": "{typography.lineHeight.moderate}",
  "modal.description.color": "{colors.textSubtle}",
  "modal.description.fontFamily": "{typography.fontFamily.body}",
  "modal.description.fontSize": "{typography.fontSize.sm}",
  "modal.description.fontWeight": "{typography.fontWeight.normal}",
  "modal.description.lineHeight": "{typography.lineHeight.short}",
  "modal.body.paddingX": "{spacing.4}",
  "modal.body.paddingLeft": "{spacing.4}",
  "modal.body.paddingRight": "{spacing.4}",
  "modal.body.paddingTop": "{spacing.2}",
  "modal.body.paddingBottom": "{spacing.4}",
  "modal.footer.paddingX": "{spacing.4}",
  "modal.footer.paddingTop": "{spacing.4}",
  "modal.footer.paddingBottom": "{spacing.4}",
  "modal.footer.gap": "{spacing.2}",
  "modal.footer.bg": "{colors.surface2}",
  "modal.footer.borderTopWidth": "{borderWidths.sm}",
  "modal.footer.borderTopStyle": "{borderStyles.solid}",
  "modal.footer.borderTopColor": "{colors.borderSubtle}"
});
var INPUT_CHARACTER_COUNT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "input-character-count.color": "{colors.textSubtle}",
  "input-character-count.fontFamily": "{typography.fontFamily.body}",
  "input-character-count.fontSize": "{typography.fontSize.xs}",
  "input-character-count.fontWeight": "{typography.fontWeight.normal}",
  "input-character-count.lineHeight": "{typography.lineHeight.short}"
});
var LINK_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "link.base.fontFamily": "{typography.fontFamily.body}",
  "link.color.primary": "{colors.accent}",
  "link.color.secondary": "{colors.textSubtle}",
  "link.color.ghost": "{colors.text}",
  "link.focus.outlineColor": "{colors.borderFocus}",
  "link.focus.outlineOffset": "{spacing.0.5}",
  "link.focus.outlineStyle": "{borderStyles.solid}",
  "link.focus.outlineWidth": "{borderWidths.md}"
});
var PROGRESS_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "progress.base.color": "{colors.accent}",
  "progress.base.track": "{colors.surface2}"
});
var SPINNER_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "spinner.base.color": "{colors.surfaceInverse}",
  "spinner.base.trackColor": "{colors.surfaceInverseAlpha32}",
  "spinner.base.animationDuration": "800ms",
  "spinner.size.xs.boxSize": "{sizes.3}",
  "spinner.size.sm.boxSize": "{sizes.4}",
  "spinner.size.md.boxSize": "{sizes.5}",
  "spinner.size.lg.boxSize": "{sizes.8}",
  "spinner.size.xl.boxSize": "{sizes.10}",
  "spinner.size.xs.borderWidth": "{borderWidths.md}",
  "spinner.size.sm.borderWidth": "{borderWidths.md}",
  "spinner.size.md.borderWidth": "3px",
  "spinner.size.lg.borderWidth": "4px",
  "spinner.size.xl.borderWidth": "4px"
});
var FIELD_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "field.horizontal.labelMinWidth": "8rem"
});
var TABLE_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "table.root.bg": "{colors.background}",
  "table.root.color": "{colors.text}",
  "table.root.borderColor": "{colors.borderSubtle}",
  "table.root.borderWidth": "{borderWidths.sm}",
  "table.root.borderStyle": "{borderStyles.solid}",
  "table.root.borderRadius": "{radii.lg}",
  "table.root.shadow": "none",
  "table.header.bg": "{colors.surface1}",
  "table.header.color": "{colors.text}",
  "table.footer.bg": "{colors.surface3Alpha32}",
  "table.stickyColumn.bg": "{colors.background}",
  "table.row.borderColor": "{colors.borderSubtle}",
  "table.row.hoverBg": "{colors.surface3Alpha32}",
  "table.row.selectedBg": "{colors.accentMuted}",
  "table.row.emphasizedBg": "{colors.surface3Alpha32}",
  "table.row.emphasizedFontWeight": "{typography.fontWeight.semibold}",
  "table.row.stripedBg": "{colors.surface2Alpha32}",
  "table.cell.color": "{colors.text}",
  "table.cell.borderColor": "{colors.borderSubtle}",
  "table.cell.fontWeight": "{typography.fontWeight.normal}",
  "table.columnHeader.fontWeight": "{typography.fontWeight.semibold}",
  "table.caption.color": "{colors.textSubtle}",
  "table.caption.fontSize": "{typography.fontSize.sm}",
  "table.size.sm.paddingX": "{spacing.2}",
  "table.size.sm.paddingY": "{spacing.1.5}",
  "table.size.sm.height": "{sizes.9}",
  "table.size.sm.fontSize": "{typography.fontSize.sm}",
  "table.size.sm.lineHeight": "{typography.lineHeight.short}",
  "table.size.md.paddingX": "{spacing.3}",
  "table.size.md.paddingY": "{spacing.2}",
  "table.size.md.height": "{sizes.11}",
  "table.size.md.fontSize": "{typography.fontSize.sm}",
  "table.size.md.lineHeight": "{typography.lineHeight.short}",
  "table.size.lg.paddingX": "{spacing.4}",
  "table.size.lg.paddingY": "{spacing.3}",
  "table.size.lg.height": "{sizes.12}",
  "table.size.lg.fontSize": "{typography.fontSize.md}",
  "table.size.lg.lineHeight": "{typography.lineHeight.moderate}"
});
var CHART_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "chart.root.bg": "{colors.background}",
  "chart.root.color": "{colors.text}",
  "chart.root.borderColor": "{colors.borderSubtle}",
  "chart.root.borderRadius": "{radii.lg}",
  "chart.root.padding": "{spacing.4}",
  "chart.root.gap": "{spacing.3}",
  "chart.title.color": "{colors.text}",
  "chart.description.color": "{colors.textSubtle}",
  "chart.axis.color": "{colors.textSubtle}",
  "chart.grid.color": "{colors.borderSubtle}",
  "chart.empty.bg": "{colors.surface2Alpha32}",
  "chart.series.1": "{colors.blue.500}",
  "chart.series.2": "{colors.green.500}",
  "chart.series.3": "{colors.yellow.500}",
  "chart.series.4": "{colors.red.500}",
  "chart.series.5": "{colors.blue.700}",
  "chart.series.6": "{colors.green.700}",
  "chart.series.7": "{colors.yellow.700}",
  "chart.series.8": "{colors.red.700}"
});
var TABS_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "tabs.list.borderColor": "{colors.border}",
  "tabs.list.gap": "calc({spacing.1} * 2.25)",
  "tabs.list.height": "{sizes.10}",
  "tabs.list.separatorLength": "{sizes.3}",
  "tabs.line.inactiveOpacity": "0.64",
  "tabs.trigger.color": "{colors.text}",
  "tabs.trigger.activeColor": "{colors.text}",
  "tabs.trigger.hoverColor": "{colors.text}",
  "tabs.trigger.fontWeight": "{typography.fontWeight.normal}",
  "tabs.trigger.fontSize": "{typography.fontSize.sm}",
  "tabs.trigger.gap": "{spacing.1}",
  "tabs.trigger.focusRing": "{colors.borderFocus}",
  "tabs.trigger.radius": "{radii.md}",
  "tabs.indicator.color": "{colors.text}",
  "tabs.indicator.width": "{borderWidths.md}",
  "tabs.content.color": "{colors.text}",
  "tabs.disabled.color": "{colors.textDisabled}",
  "tabs.line.height": "{sizes.6}",
  "tabs.subtle.height": "{sizes.7}",
  "tabs.subtle.radius": "{radii.md}",
  "tabs.subtle.hoverBackground": "{colors.accent/12}",
  "tabs.subtle.activeBackground": "{colors.accent/16}",
  "tabs.subtle.activeColor": "{colors.accent}",
  "tabs.outline.height": "{sizes.7}",
  "tabs.outline.activeHeight": "{sizes.9}",
  "tabs.outline.radius": "{radii.lg}",
  "tabs.outline.hoverBackground": "{colors.surfaceInverse/12}",
  "tabs.outline.activeBackground": "{colors.background}"
});
var ACCORDION_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "accordion.root.bg": "transparent",
  "accordion.root.color": "{colors.text}",
  "accordion.root.borderColor": "{colors.borderSubtle}",
  "accordion.root.borderWidth": "{borderWidths.sm}",
  "accordion.root.borderStyle": "{borderStyles.solid}",
  "accordion.root.borderRadius": "{radii.lg}",
  "accordion.item.bg": "transparent",
  "accordion.item.borderColor": "{colors.borderSubtle}",
  "accordion.item.openBg": "{colors.surface2Alpha32}",
  "accordion.trigger.bg": "transparent",
  "accordion.trigger.color": "{colors.text}",
  "accordion.trigger.focusRing": "{colors.borderFocus}",
  "accordion.trigger.fontWeight": "{typography.fontWeight.medium}",
  "accordion.content.bg": "transparent",
  "accordion.content.color": "{colors.textSubtle}",
  "accordion.indicator.bg": "{input.selectChevron.bg}",
  "accordion.indicator.borderRadius": "{input.selectChevron.borderRadius}",
  "accordion.indicator.color": "{input.selectChevron.color}",
  "accordion.disabled.opacity": "0.5",
  "accordion.motion.duration": "180ms",
  "accordion.motion.easing": "ease",
  "accordion.size.sm.triggerHeight": "{sizes.8}",
  "accordion.size.sm.paddingX": "{spacing.2}",
  "accordion.size.sm.paddingY": "{spacing.1.5}",
  "accordion.size.sm.fontSize": "{typography.fontSize.sm}",
  "accordion.size.sm.lineHeight": "{typography.lineHeight.short}",
  "accordion.size.sm.indicatorSize": "{sizes.4}",
  "accordion.size.md.triggerHeight": "{sizes.10}",
  "accordion.size.md.paddingX": "{spacing.3}",
  "accordion.size.md.paddingY": "{spacing.2}",
  "accordion.size.md.fontSize": "{typography.fontSize.md}",
  "accordion.size.md.lineHeight": "{typography.lineHeight.short}",
  "accordion.size.md.indicatorSize": "{sizes.4}",
  "accordion.size.lg.triggerHeight": "{sizes.12}",
  "accordion.size.lg.paddingX": "{spacing.4}",
  "accordion.size.lg.paddingY": "{spacing.3}",
  "accordion.size.lg.fontSize": "{typography.fontSize.lg}",
  "accordion.size.lg.lineHeight": "{typography.lineHeight.moderate}",
  "accordion.size.lg.indicatorSize": "{sizes.4}"
});
var FIELD_LABEL_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "field-label.requiredIndicator.color": "{colors.errorEmphasized}",
  "field-label.optional.color": "{colors.textSubtle}",
  "field-label.optional.fontWeight": "{typography.fontWeight.normal}"
});
var textSizeTokenDefaults = Object.freeze(Object.fromEntries(TYPOGRAPHY_FONT_SIZE_KEYS.flatMap((size) => {
  const style = TYPOGRAPHY_TEXT_STYLE_SCALE[size];
  return [
    [`text.size.${size}.fontSize`, `{typography.fontSize.${size}}`],
    [`text.size.${size}.lineHeight`, `{typography.lineHeight.${style.lineHeight}}`],
    [`text.size.${size}.letterSpacing`, `{typography.letterSpacing.${style.letterSpacing}}`]
  ];
})));
var TEXT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  ...textSizeTokenDefaults,
  "text.weight.thin": "{typography.fontWeight.thin}",
  "text.weight.light": "{typography.fontWeight.light}",
  "text.weight.normal": "{typography.fontWeight.normal}",
  "text.weight.medium": "{typography.fontWeight.medium}",
  "text.weight.semibold": "{typography.fontWeight.semibold}",
  "text.weight.bold": "{typography.fontWeight.bold}",
  "text.weight.extrabold": "{typography.fontWeight.extrabold}",
  "text.color.default": "{colors.text}",
  "text.color.subtle": "{colors.textSubtle}",
  "text.color.disabled": "{colors.textDisabled}",
  "text.color.inverse": "{colors.textInverse}",
  "text.color.accent": "{colors.accent}",
  "text.color.error": "{colors.error}",
  "text.color.success": "{colors.success}",
  "text.color.warning": "{colors.warning}"
});
var CODE_SIZE_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "code.base.fontWeight": "{typography.fontWeight.normal}",
  "code.base.borderWidth": "{borderWidths.sm}",
  "code.base.borderStyle": "{borderStyles.solid}",
  "code.base.borderRadius": "{radii.md}",
  "code.size.xs.fontSize": "{typography.fontSize.2xs}",
  "code.size.xs.paddingX": "{spacing.1}",
  "code.size.xs.minHeight": "{sizes.4}",
  "code.size.xs.borderRadius": "{radii.sm}",
  "code.size.sm.fontSize": "{typography.fontSize.xs}",
  "code.size.sm.paddingX": "{spacing.1.5}",
  "code.size.sm.minHeight": "{sizes.5}",
  "code.size.sm.borderRadius": "{radii.sm}",
  "code.size.md.fontSize": "{typography.fontSize.sm}",
  "code.size.md.paddingX": "{spacing.2}",
  "code.size.md.minHeight": "{sizes.6}",
  "code.size.md.borderRadius": "{radii.md}",
  "code.size.lg.fontSize": "{typography.fontSize.sm}",
  "code.size.lg.paddingX": "{spacing.3}",
  "code.size.lg.minHeight": "{sizes.7}",
  "code.size.lg.borderRadius": "{radii.md}"
});
var CODE_PALETTE_TOKEN_INPUTS = Object.freeze({
  default: {
    fg: "{colors.text}",
    subtleBg: "{colors.surface1}",
    solidBg: "{colors.text}",
    contrast: "{colors.textInverse}",
    border: "{colors.border}",
    muted: "{colors.borderSubtle}"
  },
  info: {
    fg: "{colors.infoEmphasized}",
    subtleBg: "{colors.infoMuted}",
    solidBg: "{colors.info}",
    contrast: "{colors.textInverse}",
    border: "{colors.infoSubtle}",
    muted: "{colors.infoSubtle}"
  },
  success: {
    fg: "{colors.successEmphasized}",
    subtleBg: "{colors.successMuted}",
    solidBg: "{colors.success}",
    contrast: "{colors.textDark}",
    border: "{colors.successSubtle}",
    muted: "{colors.successSubtle}"
  },
  warning: {
    fg: "{colors.warningEmphasized}",
    subtleBg: "{colors.warningMuted}",
    solidBg: "{colors.warning}",
    contrast: "{colors.textDark}",
    border: "{colors.warningSubtle}",
    muted: "{colors.warningSubtle}"
  },
  error: {
    fg: "{colors.errorEmphasized}",
    subtleBg: "{colors.errorMuted}",
    solidBg: "{colors.error}",
    contrast: "{colors.textInverse}",
    border: "{colors.errorSubtle}",
    muted: "{colors.errorSubtle}"
  }
});
var CODE_PALETTE_COMPONENT_TOKEN_DEFAULTS = Object.freeze(Object.fromEntries(Object.entries(CODE_PALETTE_TOKEN_INPUTS).flatMap(([name, values]) => Object.entries(values).map(([role, value]) => [
  `code.palette.${name}.${role}`,
  value
]))));
var CODE_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  ...CODE_SIZE_COMPONENT_TOKEN_DEFAULTS,
  ...CODE_PALETTE_COMPONENT_TOKEN_DEFAULTS
});
var TOOL_CALL_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "tool-call.toggle.background": "{colors.surface3}",
  "tool-call.toggle.color": "{colors.text}",
  "tool-call.summary.color": "{colors.textSubtle}",
  "tool-call.summary.hoverColor": "{colors.text}",
  "tool-call.target.paddingX": "{code.size.sm.paddingX}",
  "tool-call.target.minHeight": "{code.size.sm.minHeight}",
  "tool-call.target.borderRadius": "{code.size.sm.borderRadius}",
  "tool-call.target.fontSize": "{code.size.sm.fontSize}",
  "tool-call.target.fontFamily": "{typography.fontFamily.mono}",
  "tool-call.target.fontWeight": "{code.base.fontWeight}",
  "tool-call.target.borderWidth": "{code.base.borderWidth}",
  "tool-call.target.borderStyle": "{code.base.borderStyle}",
  "tool-call.target.borderColor": "{code.palette.default.muted}",
  "tool-call.target.background": "{code.palette.default.subtleBg}",
  "tool-call.target.color": "{colors.textSubtle}",
  "tool-call.status.color": "{colors.textSubtle}",
  "tool-call.status.completedColor": "{colors.success}",
  "tool-call.status.failedColor": "{colors.error}",
  "tool-call.rail.color": "{colors.border}",
  "tool-call.payload.color": "{colors.textSubtle}",
  "tool-call.runningLabel.baseColor": "{colors.textDisabled}",
  "tool-call.runningLabel.highlightColor": "{colors.text}"
});
var CODE_BLOCK_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  "code-block.base.background": "{colors.background}",
  "code-block.base.borderRadius": "{radii.lg}",
  "code-block.base.fontSize": "{typography.fontSize.md}",
  "code-block.base.paddingX": "{spacing.4}",
  "code-block.base.paddingY": "{spacing.4}",
  "code-block.header.fontSize": "{typography.fontSize.xs}",
  "code-block.title.fontSize": "{typography.fontSize.sm}",
  "code-block.code.fontSize": "{typography.fontSize.sm}",
  "code-block.code.lineHeight": "{typography.lineHeight.moderate}",
  "code-block.token.keyword": "{colors.errorEmphasized}",
  "code-block.token.string": "{colors.successEmphasized}",
  "code-block.token.comment": "{colors.textDisabled}",
  "code-block.token.number": "{colors.errorEmphasized}",
  "code-block.token.function": "{colors.successEmphasized}",
  "code-block.token.operator": "{colors.text}",
  "code-block.token.punctuation": "{colors.textSubtle}",
  "code-block.token.tag": "{colors.text}",
  "code-block.token.attrName": "{colors.info}",
  "code-block.token.attrValue": "{colors.successEmphasized}",
  "code-block.token.property": "{colors.infoEmphasized}",
  "code-block.token.type": "{colors.warningEmphasized}",
  "code-block.lineNumber.color": "{colors.textSubtle}",
  "code-block.highlight.bg": "{colors.surface1}",
  "code-block.highlight.border": "{colors.border}",
  "code-block.diff.added.bg": "{colors.successMuted}",
  "code-block.diff.added.border": "{colors.success}",
  "code-block.diff.removed.bg": "{colors.errorMuted}",
  "code-block.diff.removed.border": "{colors.error}"
});
var DEFAULT_COMPONENT_TOKEN_DEFAULTS = Object.freeze({
  ...ALERT_COMPONENT_TOKEN_DEFAULTS,
  ...TOAST_COMPONENT_TOKEN_DEFAULTS,
  ...BUTTON_COMPONENT_TOKEN_DEFAULTS,
  ...CODE_COMPONENT_TOKEN_DEFAULTS,
  ...TOOL_CALL_COMPONENT_TOKEN_DEFAULTS,
  ...CODE_BLOCK_COMPONENT_TOKEN_DEFAULTS,
  ...NAV_TREE_COMPONENT_TOKEN_DEFAULTS,
  ...BREADCRUMB_COMPONENT_TOKEN_DEFAULTS,
  ...INPUT_COMPONENT_TOKEN_DEFAULTS,
  ...INPUT_GROUP_COMPONENT_TOKEN_DEFAULTS,
  ...FLYOUT_COMPONENT_TOKEN_DEFAULTS,
  ...DROPDOWN_MENU_COMPONENT_TOKEN_DEFAULTS,
  ...MODAL_COMPONENT_TOKEN_DEFAULTS,
  ...TABLE_COMPONENT_TOKEN_DEFAULTS,
  ...CHART_COMPONENT_TOKEN_DEFAULTS,
  ...ACCORDION_COMPONENT_TOKEN_DEFAULTS,
  ...TABS_COMPONENT_TOKEN_DEFAULTS,
  ...INPUT_CHARACTER_COUNT_COMPONENT_TOKEN_DEFAULTS,
  ...FIELD_COMPONENT_TOKEN_DEFAULTS,
  ...FIELD_LABEL_COMPONENT_TOKEN_DEFAULTS,
  ...LINK_COMPONENT_TOKEN_DEFAULTS,
  ...PROGRESS_COMPONENT_TOKEN_DEFAULTS,
  ...SPINNER_COMPONENT_TOKEN_DEFAULTS,
  ...TEXT_COMPONENT_TOKEN_DEFAULTS
});

// src/theme/text-styles.ts
var TEXT_STYLE_PROPERTY_NAMES = Object.freeze([
  "fontFamily",
  "fontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing"
]);
var DEFAULT_TEXT_STYLE_ORDER = Object.freeze([
  "display",
  "h1",
  "h2",
  "h3",
  "body",
  "body-sm",
  "label",
  "caption",
  "code"
]);
var defaultTextStyles = Object.freeze({
  display: {
    base: {
      fontFamily: "heading",
      fontSize: "5xl",
      fontWeight: "bold",
      lineHeight: "shorter",
      letterSpacing: "tight"
    },
    md: {
      fontSize: "6xl"
    }
  },
  h1: {
    base: {
      fontFamily: "heading",
      fontSize: "4xl",
      fontWeight: "bold",
      lineHeight: "shorter",
      letterSpacing: "tight"
    },
    md: {
      fontSize: "5xl"
    }
  },
  h2: {
    base: {
      fontFamily: "heading",
      fontSize: "3xl",
      fontWeight: "bold",
      lineHeight: "shorter",
      letterSpacing: "normal"
    },
    md: {
      fontSize: "4xl"
    }
  },
  h3: {
    base: {
      fontFamily: "heading",
      fontSize: "2xl",
      fontWeight: "semibold",
      lineHeight: "short",
      letterSpacing: "normal"
    },
    md: {
      fontSize: "3xl"
    }
  },
  body: {
    base: {
      fontFamily: "body",
      fontSize: "sm",
      fontWeight: "normal",
      lineHeight: "short",
      letterSpacing: "normal"
    },
    md: {
      fontSize: "md",
      lineHeight: "moderate"
    }
  },
  "body-sm": {
    base: {
      fontFamily: "body",
      fontSize: "xs",
      fontWeight: "normal",
      lineHeight: "short",
      letterSpacing: "normal"
    },
    md: {
      fontSize: "sm"
    }
  },
  label: {
    base: {
      fontFamily: "body",
      fontSize: "sm",
      fontWeight: "medium",
      lineHeight: "short",
      letterSpacing: "normal"
    }
  },
  caption: {
    base: {
      fontFamily: "body",
      fontSize: "xs",
      fontWeight: "normal",
      lineHeight: "short",
      letterSpacing: "normal"
    }
  },
  code: {
    base: {
      fontFamily: "mono",
      fontSize: "sm",
      fontWeight: "normal",
      lineHeight: "short",
      letterSpacing: "normal"
    }
  }
});

// src/theme/default.ts
var DEFAULT_RADIUS_ALIAS_TOKENS = Object.freeze(Object.fromEntries(Object.entries(RADIUS_ALIAS_REFERENCES).map(([token, value]) => [`radii.${token}`, value])));
var defaultTheme = {
  breakpoints: BREAKPOINT_MIN_WIDTH,
  colors: {
    ...colors,
    ...lightSemanticColors
  },
  spacing,
  borders,
  borderWidths,
  borderStyles,
  sizes,
  typography,
  textStyles: defaultTextStyles,
  radii,
  shadows,
  zIndices,
  tokens: {
    ...DEFAULT_RADIUS_ALIAS_TOKENS,
    ...DEFAULT_COMPONENT_TOKEN_DEFAULTS
  }
};

// src/theme/compiler.ts
var THEME_SELECTOR_PATTERN = /^\[data-smth-theme=['"](?:light|dark)['"]\]$/;
var BRAND_SELECTOR_PATTERN = /^\[data-smth-brand=['"][^'"]+['"]\]$/;
var TOKEN_REFERENCE_PATTERN = /\{([^{}]+)\}/g;
var MODAL_BODY_PADDING_X_TOKEN = "modal.body.paddingX";
var MODAL_BODY_PADDING_LEFT_TOKEN = "modal.body.paddingLeft";
var MODAL_BODY_PADDING_RIGHT_TOKEN = "modal.body.paddingRight";
function compareStrings2(left, right) {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}
function normalizeTokenPathSet(input) {
  if (!input) {
    return;
  }
  return new Set(Array.from(input));
}
function isAllowedScopeSelector(selector) {
  if (selector === ":root") {
    return true;
  }
  return THEME_SELECTOR_PATTERN.test(selector) || BRAND_SELECTOR_PATTERN.test(selector);
}
function collectTokenReferencePaths(value) {
  const references = [];
  TOKEN_REFERENCE_PATTERN.lastIndex = 0;
  let match = TOKEN_REFERENCE_PATTERN.exec(value);
  while (match) {
    const parsed = parseTokenReferenceContent(match[1]);
    references.push(parsed ? `${parsed.category}.${parsed.path}` : match[1]);
    match = TOKEN_REFERENCE_PATTERN.exec(value);
  }
  return references;
}
function flattenPrimitiveTokenValues(theme) {
  const primitiveValues = new Map;
  const addPrimitiveValue = (tokenPath, value) => {
    primitiveValues.set(tokenPath, value);
  };
  addPrimitiveValue("spacing", `${SPACING_UNIT_REM}rem`);
  for (const [colorName, shades] of Object.entries(theme.colors)) {
    if (typeof shades === "string") {
      addPrimitiveValue(`colors.${colorName}`, shades);
      continue;
    }
    for (const [shade, value] of Object.entries(shades)) {
      addPrimitiveValue(`colors.${colorName}.${shade}`, value);
    }
  }
  for (const step of COMMON_SPACING_STEPS) {
    addPrimitiveValue(`spacing.${String(step)}`, spacingValue(step));
  }
  for (const [token, value] of Object.entries(theme.borders ?? {})) {
    addPrimitiveValue(`borders.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.borderWidths ?? {})) {
    addPrimitiveValue(`borderWidths.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.borderStyles ?? {})) {
    addPrimitiveValue(`borderStyles.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.sizes ?? {})) {
    addPrimitiveValue(`sizes.${token}`, value);
  }
  for (const [family, value] of Object.entries(theme.typography.fontFamily)) {
    addPrimitiveValue(`typography.fontFamily.${family}`, value);
  }
  for (const [size, value] of Object.entries(theme.typography.fontSize)) {
    addPrimitiveValue(`typography.fontSize.${size}`, `${value}rem`);
  }
  for (const [lineHeight, value] of Object.entries(theme.typography.lineHeight)) {
    addPrimitiveValue(`typography.lineHeight.${lineHeight}`, String(value));
  }
  for (const [weight, value] of Object.entries(theme.typography.fontWeight)) {
    addPrimitiveValue(`typography.fontWeight.${weight}`, String(value));
  }
  for (const [letterSpacing, value] of Object.entries(theme.typography.letterSpacing)) {
    addPrimitiveValue(`typography.letterSpacing.${letterSpacing}`, value);
  }
  for (const [token, value] of Object.entries(theme.radii)) {
    const cssValue = typeof value === "number" ? `${value}px` : value;
    addPrimitiveValue(`radii.${token}`, cssValue);
  }
  for (const [token, value] of Object.entries(theme.shadows)) {
    addPrimitiveValue(`shadows.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.zIndices ?? zIndices)) {
    addPrimitiveValue(`zIndices.${token}`, String(value));
  }
  return primitiveValues;
}
function resolveTextStylePropertyValue(propertyName, rawValue, theme, prefix) {
  if (typeof rawValue === "number") {
    return String(rawValue);
  }
  if (propertyName === "fontFamily" && Object.prototype.hasOwnProperty.call(theme.typography.fontFamily, rawValue)) {
    return `var(${tokenPathToCssVar(`typography.fontFamily.${rawValue}`, prefix)})`;
  }
  if (propertyName === "fontSize" && Object.prototype.hasOwnProperty.call(theme.typography.fontSize, rawValue)) {
    return `var(${tokenPathToCssVar(`typography.fontSize.${rawValue}`, prefix)})`;
  }
  if (propertyName === "fontWeight" && Object.prototype.hasOwnProperty.call(theme.typography.fontWeight, rawValue)) {
    return `var(${tokenPathToCssVar(`typography.fontWeight.${rawValue}`, prefix)})`;
  }
  if (propertyName === "lineHeight" && Object.prototype.hasOwnProperty.call(theme.typography.lineHeight, rawValue)) {
    return `var(${tokenPathToCssVar(`typography.lineHeight.${rawValue}`, prefix)})`;
  }
  if (propertyName === "letterSpacing" && Object.prototype.hasOwnProperty.call(theme.typography.letterSpacing, rawValue)) {
    return `var(${tokenPathToCssVar(`typography.letterSpacing.${rawValue}`, prefix)})`;
  }
  return toCssVarRef(rawValue, prefix);
}
function toTextStyleDeclarations(styleName, properties, theme, prefix) {
  const declarations = [];
  for (const propertyName of TEXT_STYLE_PROPERTY_NAMES) {
    const rawValue = properties[propertyName];
    if (rawValue === undefined) {
      continue;
    }
    declarations.push(`${tokenPathToCssVar(`textStyles.${styleName}.${propertyName}`, prefix)}: ` + `${resolveTextStylePropertyValue(propertyName, rawValue, theme, prefix)};`);
  }
  return declarations.sort(compareStrings2);
}
function toResponsiveComponentTokenDeclaration(tokenPath, rawValue, prefix) {
  return `${tokenPathToCssVar(tokenPath, prefix)}: ${toCssVarRef(rawValue, prefix)};`;
}
function sortResponsiveBlocks(blocks, breakpointOrder) {
  return blocks.sort((left, right) => {
    const leftIndex = breakpointOrder.get(left.breakpoint) ?? Number.MAX_SAFE_INTEGER;
    const rightIndex = breakpointOrder.get(right.breakpoint) ?? Number.MAX_SAFE_INTEGER;
    if (leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }
    const mediaQueryCompare = compareStrings2(left.mediaQuery, right.mediaQuery);
    if (mediaQueryCompare !== 0) {
      return mediaQueryCompare;
    }
    return compareStrings2(left.declarations[0] ?? "", right.declarations[0] ?? "");
  });
}
function collectResponsiveComponentTokenDefinitions(theme) {
  const definitions = new Map;
  const tokenEntries = Object.entries(theme.responsiveComponentTokens ?? {}).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, definition] of tokenEntries) {
    if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
      continue;
    }
    const breakpointValues = Object.entries(definition).filter(([, value]) => typeof value === "string").sort(([left], [right]) => compareStrings2(left, right));
    if (breakpointValues.length === 0) {
      continue;
    }
    definitions.set(tokenPath, Object.fromEntries(breakpointValues));
  }
  return definitions;
}
function applyLegacyModalBodyPaddingAlias(componentTokens, overrideTokens, canonicalComponentTokens, inheritedComponentTokens, honorCanonicalPaddingXOverride = false) {
  const canonicalPaddingX = canonicalComponentTokens.get(MODAL_BODY_PADDING_X_TOKEN);
  const paddingXOverride = overrideTokens.get(MODAL_BODY_PADDING_X_TOKEN);
  const inheritedPaddingX = inheritedComponentTokens?.get(MODAL_BODY_PADDING_X_TOKEN);
  const hasPaddingXOverride = overrideTokens.has(MODAL_BODY_PADDING_X_TOKEN);
  const paddingX = hasPaddingXOverride && (paddingXOverride !== canonicalPaddingX || honorCanonicalPaddingXOverride && paddingXOverride !== inheritedPaddingX) ? paddingXOverride : inheritedPaddingX !== undefined && inheritedPaddingX !== canonicalPaddingX ? inheritedPaddingX : undefined;
  if (paddingX === undefined) {
    return;
  }
  const hasExplicitLeft = overrideTokens.has(MODAL_BODY_PADDING_LEFT_TOKEN);
  const hasExplicitRight = overrideTokens.has(MODAL_BODY_PADDING_RIGHT_TOKEN);
  if (!hasExplicitLeft) {
    componentTokens.set(MODAL_BODY_PADDING_LEFT_TOKEN, paddingX);
  }
  if (!hasExplicitRight) {
    componentTokens.set(MODAL_BODY_PADDING_RIGHT_TOKEN, paddingX);
  }
}
function applyLegacyResponsiveModalBodyPaddingAlias(definitions) {
  const paddingX = definitions.get(MODAL_BODY_PADDING_X_TOKEN);
  if (!paddingX) {
    return;
  }
  definitions.set(MODAL_BODY_PADDING_LEFT_TOKEN, {
    ...paddingX,
    ...definitions.get(MODAL_BODY_PADDING_LEFT_TOKEN) ?? {}
  });
  definitions.set(MODAL_BODY_PADDING_RIGHT_TOKEN, {
    ...paddingX,
    ...definitions.get(MODAL_BODY_PADDING_RIGHT_TOKEN) ?? {}
  });
}
function filterResponsiveComponentTokenDefinitions(definitions, allowedTokenPaths) {
  if (!allowedTokenPaths) {
    return definitions;
  }
  const filtered = new Map;
  for (const [tokenPath, definition] of definitions.entries()) {
    if (allowedTokenPaths.has(tokenPath)) {
      filtered.set(tokenPath, definition);
    }
  }
  return filtered;
}
function expandComponentTokenDependencies(flatTokenValues, responsiveTokenDefinitions, seedTokenPaths) {
  const expanded = new Set;
  const queue = Array.from(seedTokenPaths).sort(compareStrings2);
  while (queue.length > 0) {
    const tokenPath = queue.shift();
    if (!tokenPath || expanded.has(tokenPath)) {
      continue;
    }
    expanded.add(tokenPath);
    const candidateValues = [];
    const flatValue = flatTokenValues.get(tokenPath);
    if (flatValue) {
      candidateValues.push(flatValue);
    }
    const responsiveDefinition = responsiveTokenDefinitions.get(tokenPath);
    if (responsiveDefinition) {
      candidateValues.push(...Object.values(responsiveDefinition));
    }
    for (const candidateValue of candidateValues) {
      for (const referencePath of collectTokenReferencePaths(candidateValue)) {
        if (!expanded.has(referencePath) && (flatTokenValues.has(referencePath) || responsiveTokenDefinitions.has(referencePath))) {
          queue.push(referencePath);
        }
      }
    }
  }
  return expanded;
}
function applyResponsiveComponentTokenBaseOverrides(flatTokenValues, responsiveTokenDefinitions) {
  const tokenEntries = Array.from(responsiveTokenDefinitions.entries()).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, definition] of tokenEntries) {
    const baseValue = definition.base;
    if (typeof baseValue === "string") {
      flatTokenValues.set(tokenPath, baseValue);
    }
  }
}
function buildResponsiveComponentTokenBlocks(definitions, theme, prefix) {
  const responsiveBlocks = [];
  const breakpointOrder = new Map(getNonBaseBreakpointOrder(theme).map((breakpoint, index) => [breakpoint, index]));
  const tokenEntries = Array.from(definitions.entries()).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, definition] of tokenEntries) {
    const breakpointEntries = Object.entries(definition).filter(([breakpoint]) => breakpoint !== "base").sort(([left], [right]) => {
      const leftIndex = breakpointOrder.get(left) ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = breakpointOrder.get(right) ?? Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
      return compareStrings2(left, right);
    });
    for (const [breakpoint, rawValue] of breakpointEntries) {
      const breakpointWidth = theme.breakpoints[breakpoint];
      if (!breakpointWidth) {
        continue;
      }
      responsiveBlocks.push({
        breakpoint,
        mediaQuery: `(min-width: ${breakpointWidth})`,
        declarations: [toResponsiveComponentTokenDeclaration(tokenPath, rawValue, prefix)]
      });
    }
  }
  return sortResponsiveBlocks(responsiveBlocks, breakpointOrder);
}
function collectTextStyleScopeDeclarations(scopeInputs, prefix) {
  return scopeInputs.map((scopeInput) => {
    const baseDeclarations = [];
    const responsiveBlocks = [];
    const breakpointOrder = new Map(getNonBaseBreakpointOrder(scopeInput.theme).map((breakpoint, index) => [breakpoint, index]));
    const textStyleEntries = Object.entries(scopeInput.theme.textStyles ?? {}).sort(([left], [right]) => compareStrings2(left, right));
    for (const [styleName, definition] of textStyleEntries) {
      const baseProperties = definition.base;
      if (baseProperties) {
        baseDeclarations.push(...toTextStyleDeclarations(styleName, baseProperties, scopeInput.theme, prefix));
      }
      const breakpointEntries = Object.entries(definition).filter(([breakpoint]) => breakpoint !== "base").sort(([left], [right]) => {
        const leftIndex = breakpointOrder.get(left) ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = breakpointOrder.get(right) ?? Number.MAX_SAFE_INTEGER;
        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }
        return compareStrings2(left, right);
      });
      for (const [breakpoint, properties] of breakpointEntries) {
        const breakpointWidth = scopeInput.theme.breakpoints[breakpoint];
        if (!breakpointWidth || !properties) {
          continue;
        }
        const declarations = toTextStyleDeclarations(styleName, properties, scopeInput.theme, prefix);
        if (declarations.length === 0) {
          continue;
        }
        responsiveBlocks.push({
          breakpoint,
          mediaQuery: `(min-width: ${breakpointWidth})`,
          declarations
        });
      }
    }
    return {
      selector: scopeInput.selector,
      baseDeclarations: baseDeclarations.sort(compareStrings2),
      responsiveBlocks: sortResponsiveBlocks(responsiveBlocks, breakpointOrder)
    };
  });
}
function mergeExtensionTokenSources(sources, diagnostics) {
  const merged = new Map;
  const sortedSources = [...sources].sort((left, right) => compareStrings2(left.sourceId, right.sourceId));
  for (const source of sortedSources) {
    const tokenEntries = Object.entries(source.tokens).sort(([left], [right]) => compareStrings2(left, right));
    for (const [tokenPath, tokenValue] of tokenEntries) {
      const existingValue = merged.get(tokenPath);
      if (existingValue === undefined) {
        merged.set(tokenPath, tokenValue);
        continue;
      }
      if (existingValue === tokenValue) {
        continue;
      }
      diagnostics.push({
        code: "theme.tokens.collision",
        severity: "error",
        tokenPath,
        sourceId: source.sourceId,
        message: `Extension token collision for '${tokenPath}': existing value '${existingValue}' ` + `differs from '${tokenValue}'.`
      });
    }
  }
  return merged;
}
function toCssDeclarations(tokenValues, prefix) {
  const declarations = [];
  const sortedEntries = Array.from(tokenValues.entries()).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, rawValue] of sortedEntries) {
    const cssVariable = tokenPathToCssVar(tokenPath, prefix);
    declarations.push(`${cssVariable}: ${toCssVarRef(rawValue, prefix)};`);
  }
  return declarations;
}
function filterTokenValues(tokenValues, allowedTokenPaths) {
  if (!allowedTokenPaths) {
    return tokenValues;
  }
  const filtered = new Map;
  for (const [tokenPath, tokenValue] of tokenValues.entries()) {
    if (allowedTokenPaths.has(tokenPath)) {
      filtered.set(tokenPath, tokenValue);
    }
  }
  return filtered;
}
function expandTokenPathDependencies(tokenValues, seedTokenPaths) {
  const expanded = new Set;
  const queue = Array.from(seedTokenPaths).sort(compareStrings2);
  while (queue.length > 0) {
    const tokenPath = queue.shift();
    if (!tokenPath) {
      continue;
    }
    if (expanded.has(tokenPath)) {
      continue;
    }
    expanded.add(tokenPath);
    const tokenValue = tokenValues.get(tokenPath);
    if (!tokenValue) {
      continue;
    }
    const references = collectTokenReferencePaths(tokenValue).sort(compareStrings2);
    for (const referencePath of references) {
      if (!tokenValues.has(referencePath) || expanded.has(referencePath)) {
        continue;
      }
      queue.push(referencePath);
    }
  }
  return expanded;
}
function diffTokenValues(tokenValues, baselineValues) {
  const diffed = new Map;
  for (const [tokenPath, tokenValue] of tokenValues.entries()) {
    if (baselineValues.get(tokenPath) === tokenValue) {
      continue;
    }
    diffed.set(tokenPath, tokenValue);
  }
  return diffed;
}
function diffTextStyleDeclarations(declarations, baselineDeclarations) {
  const baselineDeclarationSet = new Set(baselineDeclarations);
  return declarations.filter((declaration) => !baselineDeclarationSet.has(declaration));
}
function diffResponsiveDeclarationBlocks(blocks, baselineBlocks) {
  const baselineDeclarationsByMediaQuery = new Map;
  for (const block of baselineBlocks) {
    const declarations = baselineDeclarationsByMediaQuery.get(block.mediaQuery) ?? new Set;
    baselineDeclarationsByMediaQuery.set(block.mediaQuery, declarations);
    for (const declaration of block.declarations) {
      declarations.add(declaration);
    }
  }
  const diffedBlocks = [];
  for (const block of blocks) {
    const baselineDeclarations = baselineDeclarationsByMediaQuery.get(block.mediaQuery);
    const declarations = baselineDeclarations ? diffTextStyleDeclarations(block.declarations, [...baselineDeclarations]) : [...block.declarations];
    if (declarations.length === 0) {
      continue;
    }
    diffedBlocks.push({
      breakpoint: block.breakpoint,
      mediaQuery: block.mediaQuery,
      declarations
    });
  }
  return diffedBlocks;
}
function renderResponsiveBlocks(selector, blocks, minify) {
  const chunks = [];
  for (const block of blocks) {
    if (minify) {
      chunks.push(`@media ${block.mediaQuery}{${selector}{${block.declarations.join("")}}}`);
      continue;
    }
    chunks.push([
      `@media ${block.mediaQuery} {`,
      `  ${selector} {`,
      ...block.declarations.map((declaration) => `    ${declaration}`),
      "  }",
      "}"
    ].join(`
`));
  }
  return chunks;
}
function renderCssFromScopes(selectorDeclarations, pickDeclarations, minify, prefix) {
  const chunks = [];
  for (const scope of selectorDeclarations) {
    const declarations = toCssDeclarations(new Map(pickDeclarations(scope)), prefix);
    if (declarations.length === 0) {
      continue;
    }
    if (minify) {
      chunks.push(`${scope.selector}{${declarations.join("")}}`);
    } else {
      chunks.push([
        `${scope.selector} {`,
        ...declarations.map((declaration) => `  ${declaration}`),
        "}"
      ].join(`
`));
    }
  }
  if (chunks.length === 0) {
    return "";
  }
  return minify ? chunks.join("") : `${chunks.join(`

`)}
`;
}
function renderTextStyleCssFromScopes(scopeDeclarations, minify) {
  const chunks = [];
  for (const scope of scopeDeclarations) {
    if (scope.baseDeclarations.length > 0) {
      if (minify) {
        chunks.push(`${scope.selector}{${scope.baseDeclarations.join("")}}`);
      } else {
        chunks.push([
          `${scope.selector} {`,
          ...scope.baseDeclarations.map((declaration) => `  ${declaration}`),
          "}"
        ].join(`
`));
      }
    }
    chunks.push(...renderResponsiveBlocks(scope.selector, scope.responsiveBlocks, minify));
  }
  if (chunks.length === 0) {
    return "";
  }
  return minify ? chunks.join("") : `${chunks.join(`

`)}
`;
}
function renderResponsiveComponentTokenCssFromScopes(scopeDeclarations, minify) {
  const chunks = [];
  for (const scope of scopeDeclarations) {
    chunks.push(...renderResponsiveBlocks(scope.selector, scope.responsiveComponentBlocks, minify));
  }
  if (chunks.length === 0) {
    return "";
  }
  return minify ? chunks.join("") : `${chunks.join(`

`)}
`;
}
function compileScopeDeclarations(scopeInput, options) {
  const primitiveTokenValues = flattenPrimitiveTokenValues(scopeInput.theme);
  const mergedComponentTokens = new Map(options.defaultComponentTokens);
  const componentTokenOverrides = new Map;
  const responsiveComponentTokenDefinitions = collectResponsiveComponentTokenDefinitions(scopeInput.theme);
  applyLegacyResponsiveModalBodyPaddingAlias(responsiveComponentTokenDefinitions);
  const sortedExtensionEntries = Array.from(options.extensionTokens.entries()).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, tokenValue] of sortedExtensionEntries) {
    mergedComponentTokens.set(tokenPath, tokenValue);
    componentTokenOverrides.set(tokenPath, tokenValue);
  }
  const themeTokenEntries = Object.entries(scopeInput.theme.tokens ?? {}).sort(([left], [right]) => compareStrings2(left, right));
  for (const [tokenPath, tokenValue] of themeTokenEntries) {
    const extensionTokenValue = options.extensionTokens.get(tokenPath);
    if (extensionTokenValue !== undefined && extensionTokenValue !== tokenValue) {
      options.diagnostics.push({
        code: "theme.tokens.collision",
        severity: "error",
        scopeId: scopeInput.scopeId,
        tokenPath,
        sourceId: "theme.tokens",
        message: `Theme token collision for '${tokenPath}' in scope '${scopeInput.scopeId}': ` + `extension value '${extensionTokenValue}' differs from theme value '${tokenValue}'.`
      });
    }
    mergedComponentTokens.set(tokenPath, tokenValue);
    componentTokenOverrides.set(tokenPath, tokenValue);
  }
  applyLegacyModalBodyPaddingAlias(mergedComponentTokens, componentTokenOverrides, options.canonicalComponentTokens, options.defaultComponentTokens, scopeInput.scopeId !== "default");
  const expandedComponentAllowList = options.usedComponentTokenPaths ? expandComponentTokenDependencies(mergedComponentTokens, responsiveComponentTokenDefinitions, options.usedComponentTokenPaths) : undefined;
  applyResponsiveComponentTokenBaseOverrides(mergedComponentTokens, responsiveComponentTokenDefinitions);
  const filteredComponentTokens = filterTokenValues(mergedComponentTokens, expandedComponentAllowList);
  const filteredResponsiveComponentTokenDefinitions = filterResponsiveComponentTokenDefinitions(responsiveComponentTokenDefinitions, expandedComponentAllowList);
  const primitiveAllowList = options.usedPrimitiveTokenPaths || expandedComponentAllowList ? new Set(options.usedPrimitiveTokenPaths ?? []) : undefined;
  if (primitiveAllowList) {
    for (const tokenValue of filteredComponentTokens.values()) {
      for (const referencePath of collectTokenReferencePaths(tokenValue)) {
        primitiveAllowList.add(referencePath);
      }
    }
    for (const definition of filteredResponsiveComponentTokenDefinitions.values()) {
      for (const tokenValue of Object.values(definition)) {
        for (const referencePath of collectTokenReferencePaths(tokenValue)) {
          primitiveAllowList.add(referencePath);
        }
      }
    }
  }
  const expandedPrimitiveAllowList = primitiveAllowList ? expandTokenPathDependencies(primitiveTokenValues, primitiveAllowList) : undefined;
  const filteredPrimitiveTokens = filterTokenValues(primitiveTokenValues, expandedPrimitiveAllowList);
  return {
    selector: scopeInput.selector,
    variables: filteredPrimitiveTokens,
    componentVariables: filteredComponentTokens,
    responsiveComponentBlocks: buildResponsiveComponentTokenBlocks(filteredResponsiveComponentTokenDefinitions, scopeInput.theme, options.prefix)
  };
}
function compileThemeCss(input) {
  const prefix = input.prefix ?? "--smth";
  const minify = input.minify ?? false;
  const diagnostics = [];
  const extensionTokens = mergeExtensionTokenSources(input.extensionTokenSources ?? [], diagnostics);
  const scopeInputs = [{
    scopeId: "default",
    selector: ":root",
    theme: input.defaultTheme
  }];
  const seenSelectors = new Set([":root"]);
  const scopes = input.scopes ?? {};
  const sortedScopeEntries = Object.entries(scopes).sort(([left], [right]) => compareStrings2(left, right));
  for (const [scopeId, scopeConfig] of sortedScopeEntries) {
    if (!isAllowedScopeSelector(scopeConfig.selector) || scopeConfig.selector === ":root") {
      diagnostics.push({
        code: "theme.scope.invalid_selector",
        severity: "error",
        scopeId,
        message: `Scope '${scopeId}' has invalid selector '${scopeConfig.selector}'. ` + `Allowed selectors are [data-smth-theme='light'|'dark'] and [data-smth-brand='...'].`
      });
      continue;
    }
    if (seenSelectors.has(scopeConfig.selector)) {
      diagnostics.push({
        code: "theme.scope.duplicate_selector",
        severity: "error",
        scopeId,
        message: `Scope '${scopeId}' duplicates selector '${scopeConfig.selector}'.`
      });
      continue;
    }
    seenSelectors.add(scopeConfig.selector);
    scopeInputs.push({
      scopeId,
      selector: scopeConfig.selector,
      theme: scopeConfig.theme
    });
  }
  const usedPrimitiveTokenPaths = normalizeTokenPathSet(input.usedTokenManifest?.primitiveTokenPaths);
  const usedComponentTokenPaths = normalizeTokenPathSet(input.usedTokenManifest?.componentTokenPaths);
  const canonicalComponentTokens = new Map(Object.entries(defaultTheme.tokens ?? {}).sort(([left], [right]) => compareStrings2(left, right)));
  const defaultComponentTokens = new Map(Object.entries({
    ...Object.fromEntries(canonicalComponentTokens),
    ...input.defaultTheme.tokens ?? {}
  }).sort(([left], [right]) => compareStrings2(left, right)));
  applyLegacyModalBodyPaddingAlias(defaultComponentTokens, new Map(Object.entries(input.defaultTheme.tokens ?? {})), canonicalComponentTokens);
  const scopeDeclarations = scopeInputs.map((scopeInput) => compileScopeDeclarations(scopeInput, {
    prefix,
    defaultComponentTokens,
    canonicalComponentTokens,
    extensionTokens,
    diagnostics,
    usedPrimitiveTokenPaths,
    usedComponentTokenPaths
  }));
  const textStyleScopeDeclarations = collectTextStyleScopeDeclarations(scopeInputs, prefix);
  const defaultScopeDeclarations = scopeDeclarations[0];
  const minimalScopeDeclarations = scopeDeclarations.map((scopeDeclarationsEntry, index) => {
    if (index === 0 || !defaultScopeDeclarations) {
      return scopeDeclarationsEntry;
    }
    return {
      ...scopeDeclarationsEntry,
      variables: diffTokenValues(scopeDeclarationsEntry.variables, defaultScopeDeclarations.variables),
      componentVariables: diffTokenValues(scopeDeclarationsEntry.componentVariables, defaultScopeDeclarations.componentVariables),
      responsiveComponentBlocks: diffResponsiveDeclarationBlocks(scopeDeclarationsEntry.responsiveComponentBlocks, defaultScopeDeclarations.responsiveComponentBlocks)
    };
  });
  const defaultTextStyleScopeDeclarations = textStyleScopeDeclarations[0];
  const minimalTextStyleScopeDeclarations = textStyleScopeDeclarations.map((scopeDeclarationsEntry, index) => {
    if (index === 0 || !defaultTextStyleScopeDeclarations) {
      return scopeDeclarationsEntry;
    }
    return {
      ...scopeDeclarationsEntry,
      baseDeclarations: diffTextStyleDeclarations(scopeDeclarationsEntry.baseDeclarations, defaultTextStyleScopeDeclarations.baseDeclarations),
      responsiveBlocks: diffResponsiveDeclarationBlocks(scopeDeclarationsEntry.responsiveBlocks, defaultTextStyleScopeDeclarations.responsiveBlocks)
    };
  });
  const orderedDiagnostics = [...diagnostics].sort((left, right) => {
    const codeCompare = compareStrings2(left.code, right.code);
    if (codeCompare !== 0) {
      return codeCompare;
    }
    const scopeCompare = compareStrings2(left.scopeId ?? "", right.scopeId ?? "");
    if (scopeCompare !== 0) {
      return scopeCompare;
    }
    const tokenCompare = compareStrings2(left.tokenPath ?? "", right.tokenPath ?? "");
    if (tokenCompare !== 0) {
      return tokenCompare;
    }
    return compareStrings2(left.message, right.message);
  });
  return {
    variablesCss: renderCssFromScopes(minimalScopeDeclarations, (scope) => scope.variables, minify, prefix),
    componentVariablesCss: renderCssFromScopes(minimalScopeDeclarations, (scope) => scope.componentVariables, minify, prefix) + renderResponsiveComponentTokenCssFromScopes(minimalScopeDeclarations, minify) + renderTextStyleCssFromScopes(minimalTextStyleScopeDeclarations, minify),
    diagnostics: orderedDiagnostics
  };
}

// src/utils/theme-utils.ts
function mergeTheme(base, override) {
  return {
    breakpoints: override.breakpoints ? sortBreakpointScale({ ...base.breakpoints, ...override.breakpoints }) : base.breakpoints,
    colors: override.colors ? mergeColorPalette(base.colors, override.colors) : base.colors,
    spacing: base.spacing,
    borders: override.borders ? { ...base.borders, ...override.borders } : base.borders,
    borderWidths: override.borderWidths ? { ...base.borderWidths, ...override.borderWidths } : base.borderWidths,
    borderStyles: override.borderStyles ? { ...base.borderStyles, ...override.borderStyles } : base.borderStyles,
    sizes: override.sizes ? { ...base.sizes, ...override.sizes } : base.sizes,
    typography: override.typography ? mergeTypography(base.typography, override.typography) : base.typography,
    textStyles: override.textStyles ? mergeTextStyles(base.textStyles, override.textStyles) : base.textStyles,
    radii: override.radii ? { ...base.radii, ...override.radii } : base.radii,
    shadows: override.shadows ? { ...base.shadows, ...override.shadows } : base.shadows,
    zIndices: override.zIndices ? { ...base.zIndices ?? zIndices, ...override.zIndices } : base.zIndices ?? zIndices,
    tokens: mergeThemeTokens(base.tokens, override.tokens),
    responsiveComponentTokens: mergeResponsiveComponentTokens(base.responsiveComponentTokens, override.responsiveComponentTokens)
  };
}
function mergeColorPalette(base, override) {
  const result = {
    ...base
  };
  for (const [colorName, overrideValue] of Object.entries(override)) {
    if (overrideValue === undefined) {
      continue;
    }
    if (typeof overrideValue === "string") {
      result[colorName] = overrideValue;
      continue;
    }
    const baseValue = result[colorName];
    if (baseValue && typeof baseValue === "object") {
      result[colorName] = { ...baseValue, ...overrideValue };
    } else {
      result[colorName] = overrideValue;
    }
  }
  return result;
}
function mergeThemeTokens(base, override) {
  if (!base && !override) {
    return;
  }
  return {
    ...base ?? {},
    ...override ?? {}
  };
}
function mergeResponsiveComponentTokens(base, override) {
  if (!base && !override) {
    return;
  }
  const result = {};
  for (const [tokenPath, definition] of Object.entries(base ?? {})) {
    if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
      continue;
    }
    result[tokenPath] = Object.fromEntries(Object.entries(definition).filter(([, value]) => typeof value === "string"));
  }
  for (const [tokenPath, definition] of Object.entries(override ?? {})) {
    if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
      continue;
    }
    const nextDefinition = Object.fromEntries(Object.entries(definition).filter(([, value]) => typeof value === "string"));
    result[tokenPath] = {
      ...result[tokenPath] ?? {},
      ...nextDefinition
    };
  }
  return result;
}
function mergeTypography(base, override) {
  return {
    fontFamily: override.fontFamily ? { ...base.fontFamily, ...override.fontFamily } : base.fontFamily,
    fontSize: override.fontSize ? { ...base.fontSize, ...override.fontSize } : base.fontSize,
    lineHeight: override.lineHeight ? { ...base.lineHeight, ...override.lineHeight } : base.lineHeight,
    fontWeight: override.fontWeight ? { ...base.fontWeight, ...override.fontWeight } : base.fontWeight,
    letterSpacing: override.letterSpacing ? { ...base.letterSpacing, ...override.letterSpacing } : base.letterSpacing
  };
}
function mergeTextStyles(base, override) {
  const result = {
    ...base
  };
  for (const [styleName, styleDefinition] of Object.entries(override)) {
    if (!styleDefinition || typeof styleDefinition !== "object") {
      continue;
    }
    const mergedDefinition = {};
    const baseDefinition = base[styleName] ?? {};
    for (const [breakpoint, properties] of Object.entries(baseDefinition)) {
      if (properties) {
        mergedDefinition[breakpoint] = { ...properties };
      }
    }
    for (const [breakpoint, properties] of Object.entries(styleDefinition)) {
      if (!properties || typeof properties !== "object") {
        continue;
      }
      mergedDefinition[breakpoint] = {
        ...mergedDefinition[breakpoint] ?? {},
        ...properties
      };
    }
    result[styleName] = mergedDefinition;
  }
  return result;
}

// src/resolve/token-map.ts
function buildTokenMap(theme, recipeTokens) {
  const map = new Map;
  for (const [name, shades] of Object.entries(theme.colors)) {
    if (typeof shades === "string") {
      map.set(`colors.${name}`, shades);
      continue;
    }
    for (const [shade, value] of Object.entries(shades)) {
      map.set(`colors.${name}.${shade}`, value);
    }
  }
  const typo = theme.typography;
  for (const [family, value] of Object.entries(typo.fontFamily)) {
    map.set(`typography.fontFamily.${family}`, value);
  }
  for (const [size, value] of Object.entries(typo.fontSize)) {
    map.set(`typography.fontSize.${size}`, `${value}rem`);
  }
  for (const [lineHeight, value] of Object.entries(typo.lineHeight)) {
    map.set(`typography.lineHeight.${lineHeight}`, String(value));
  }
  for (const [weight, value] of Object.entries(typo.fontWeight)) {
    map.set(`typography.fontWeight.${weight}`, value);
  }
  for (const [letterSpacing, value] of Object.entries(typo.letterSpacing)) {
    map.set(`typography.letterSpacing.${letterSpacing}`, value);
  }
  for (const [token, value] of Object.entries(theme.sizes ?? {})) {
    map.set(`sizes.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.borders ?? {})) {
    map.set(`borders.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.borderWidths ?? {})) {
    map.set(`borderWidths.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.borderStyles ?? {})) {
    map.set(`borderStyles.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.radii)) {
    map.set(`radii.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.shadows)) {
    map.set(`shadows.${token}`, value);
  }
  for (const [token, value] of Object.entries(theme.zIndices ?? zIndices)) {
    map.set(`zIndices.${token}`, value);
  }
  if (recipeTokens) {
    for (const [path, value] of Object.entries(recipeTokens)) {
      map.set(path, value);
    }
  }
  if (theme.tokens) {
    for (const [path, value] of Object.entries(theme.tokens)) {
      map.set(path, value);
    }
  }
  return map;
}

// src/utils/token-helpers.ts
function colorWithOpacity(color, opacity) {
  const clampedOpacity = Math.max(0, Math.min(1, opacity));
  let hex = color.replace("#", "");
  if (hex.length === 3) {
    hex = hex.split("").map((c) => c + c).join("");
  } else if (hex.length === 4) {
    hex = hex.slice(0, 3).split("").map((c) => c + c).join("");
  } else if (hex.length === 8) {
    hex = hex.slice(0, 6);
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
}

// src/resolve/resolve.ts
var MAX_DEPTH = 10;
function resolveToken(ref, map, depth = 0) {
  if (depth > MAX_DEPTH)
    return ref;
  const applyOpacity = (resolved, opacity, original) => {
    if (typeof resolved !== "string") {
      return original;
    }
    if (resolved.includes("{")) {
      return original;
    }
    if (!resolved.startsWith("#")) {
      return original;
    }
    return colorWithOpacity(resolved, opacity / 100);
  };
  const standalone = parseStandaloneTokenReference(ref);
  if (standalone) {
    const resolved = lookupPath(`${standalone.category}.${standalone.path}`, map, depth);
    if (resolved === undefined) {
      return ref;
    }
    if (standalone.opacity !== undefined) {
      return applyOpacity(resolved, standalone.opacity, ref);
    }
    return resolved ?? ref;
  }
  if (hasEmbeddedRefs(ref)) {
    return ref.replace(createEmbeddedRefsGlobal(), (_match, referenceContent) => {
      const parsed = parseTokenReferenceContent(referenceContent);
      if (!parsed) {
        return `{${referenceContent}}`;
      }
      const resolved = lookupPath(`${parsed.category}.${parsed.path}`, map, depth);
      if (resolved === undefined) {
        return `{${referenceContent}}`;
      }
      if (parsed.opacity !== undefined) {
        return String(applyOpacity(resolved, parsed.opacity, `{${referenceContent}}`));
      }
      return String(resolved);
    });
  }
  return ref;
}
function lookupPath(path, map, depth) {
  if (path.startsWith("spacing.")) {
    const spacingKey = path.slice(8);
    const n = Number(spacingKey);
    if (isSupportedSpacingStep(n))
      return spacingValue(n);
  }
  const value = map.get(path);
  if (value === undefined)
    return;
  if (typeof value === "string" && value.includes("{")) {
    return resolveToken(value, map, depth + 1);
  }
  return value;
}
function resolveDeep(value, map, visited = new Set) {
  if (typeof value === "string") {
    if (!hasEmbeddedRefs(value))
      return value;
    return resolveToken(value, map);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (visited.has(value))
    return value;
  visited.add(value);
  if (Array.isArray(value)) {
    let changed2 = false;
    const result = value.map((item) => {
      const resolved2 = resolveDeep(item, map, visited);
      if (resolved2 !== item)
        changed2 = true;
      return resolved2;
    });
    return changed2 ? result : value;
  }
  let changed = false;
  const resolved = {};
  for (const [k, v] of Object.entries(value)) {
    const r = resolveDeep(v, map, visited);
    resolved[k] = r;
    if (r !== v)
      changed = true;
  }
  return changed ? resolved : value;
}

// src/resolve/index.ts
function resolveReferences(theme) {
  const map = buildTokenMap(theme);
  const resolved = resolveDeep(theme, map);
  if (!theme.tokens && !theme.responsiveComponentTokens) {
    return resolved;
  }
  return {
    ...resolved,
    ...theme.tokens ? { tokens: { ...theme.tokens } } : {},
    ...theme.responsiveComponentTokens ? { responsiveComponentTokens: { ...theme.responsiveComponentTokens } } : {}
  };
}

// src/theme/validate.ts
function isValidColor(value) {
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)) {
    return true;
  }
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/i.test(value)) {
    return true;
  }
  if (/^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/i.test(value)) {
    return true;
  }
  return false;
}
function isValidBreakpointWidth(value) {
  return /^-?(?:\d+\.?\d*|\.\d+)(?:px|rem|em|vw|vh|svw|svh|lvw|lvh|dvw|dvh)$/.test(value.trim());
}
var TEXT_STYLE_PROPERTY_NAME_SET = new Set(TEXT_STYLE_PROPERTY_NAMES);
function canResolveReference(reference, map) {
  const parsed = parseTokenReferenceContent(reference.slice(1, -1));
  if (!parsed) {
    return false;
  }
  if (parsed.opacity !== undefined && parsed.category !== "colors") {
    return false;
  }
  const fullPath = `${parsed.category}.${parsed.path}`;
  if (map.has(fullPath))
    return true;
  switch (parsed.category) {
    case "colors":
      return false;
    case "spacing": {
      const n = Number(parsed.path);
      if (!Number.isNaN(n) && n >= 0)
        return true;
      return false;
    }
    default:
      return false;
  }
}
function detectCircularReferences(theme) {
  const circular = [];
  const visiting = new Set;
  const visited = new Set;
  function visit(value, path) {
    if (typeof value !== "string") {
      return;
    }
    if (!value.startsWith("{") || !value.endsWith("}")) {
      return;
    }
    const parsed = parseTokenReferenceContent(value.slice(1, -1));
    if (!parsed) {
      return;
    }
    if (visiting.has(value)) {
      circular.push(path);
      return;
    }
    if (visited.has(value)) {
      return;
    }
    visiting.add(value);
    let current = theme;
    const parts = [parsed.category, ...parsed.path.split(".")];
    for (const part of parts) {
      if (current && typeof current === "object" && part in current) {
        current = current[part];
      } else {
        break;
      }
    }
    if (typeof current === "string" && current.startsWith("{") && current.endsWith("}")) {
      visit(current, `${path} -> ${value}`);
    }
    visiting.delete(value);
    visited.add(value);
  }
  function traverse(obj, path = "") {
    if (obj && typeof obj === "object") {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        visit(value, currentPath);
        traverse(value, currentPath);
      }
    }
  }
  traverse(theme);
  return circular;
}
function validateTheme(theme) {
  const errors = [];
  const warnings = [];
  const requiredKeys = [
    "breakpoints",
    "colors",
    "spacing",
    "sizes",
    "borders",
    "borderWidths",
    "borderStyles",
    "typography",
    "textStyles",
    "radii",
    "shadows",
    "zIndices"
  ];
  for (const key of requiredKeys) {
    if (!(key in theme)) {
      errors.push({
        severity: "error",
        path: key,
        message: `Missing required property '${key}'`
      });
    }
  }
  const lookup = buildTokenMap(theme);
  if (theme.breakpoints) {
    for (const [breakpointName, breakpointWidth] of Object.entries(theme.breakpoints)) {
      if (!isValidBreakpointWidth(breakpointWidth)) {
        errors.push({
          severity: "error",
          path: `breakpoints.${breakpointName}`,
          message: "Breakpoint width must be a CSS length such as 48rem or 768px",
          value: breakpointWidth
        });
      }
    }
  }
  if (theme.colors) {
    for (const [colorName, shades] of Object.entries(theme.colors)) {
      if (typeof shades === "string") {
        const path = `colors.${colorName}`;
        if (RE_ANY_REF.test(shades)) {
          if (!canResolveReference(shades, lookup)) {
            errors.push({
              severity: "error",
              path,
              message: `Cannot resolve color reference: ${shades}`,
              value: shades
            });
          }
        } else if (!isValidColor(shades)) {
          warnings.push({
            severity: "warning",
            path,
            message: `Invalid color format: ${shades}`,
            value: shades
          });
        }
        continue;
      }
      if (!shades || typeof shades !== "object") {
        errors.push({
          severity: "error",
          path: `colors.${colorName}`,
          message: "Color must be a string or an object with shades",
          value: shades
        });
        continue;
      }
      for (const [shade, value] of Object.entries(shades)) {
        const path = `colors.${colorName}.${shade}`;
        if (typeof value === "string" && RE_ANY_REF.test(value)) {
          if (!canResolveReference(value, lookup)) {
            errors.push({
              severity: "error",
              path,
              message: `Cannot resolve color reference: ${value}`,
              value
            });
          }
        } else if (typeof value === "string") {
          if (!isValidColor(value)) {
            warnings.push({
              severity: "warning",
              path,
              message: `Invalid color format: ${value}`,
              value
            });
          }
        } else {
          errors.push({
            severity: "error",
            path,
            message: "Color value must be a string",
            value
          });
        }
      }
    }
  }
  if (theme.spacing) {
    for (const [token, value] of Object.entries(theme.spacing)) {
      const path = `spacing.${token}`;
      if (typeof value !== "number") {
        errors.push({
          severity: "error",
          path,
          message: "Spacing value must be a number",
          value
        });
      } else if (value < 0) {
        warnings.push({
          severity: "warning",
          path,
          message: "Spacing value is negative",
          value
        });
      }
    }
  }
  if (theme.sizes) {
    for (const [token, value] of Object.entries(theme.sizes)) {
      const path = `sizes.${token}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Size value must be a string",
          value
        });
      }
    }
  }
  if (theme.borders) {
    for (const [token, value] of Object.entries(theme.borders)) {
      const path = `borders.${token}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Border value must be a string",
          value
        });
      }
    }
  }
  if (theme.borderWidths) {
    for (const [token, value] of Object.entries(theme.borderWidths)) {
      const path = `borderWidths.${token}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Border width value must be a string",
          value
        });
      }
    }
  }
  if (theme.borderStyles) {
    for (const [token, value] of Object.entries(theme.borderStyles)) {
      const path = `borderStyles.${token}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Border style value must be a string",
          value
        });
      }
    }
  }
  if (theme.radii) {
    for (const [token, value] of Object.entries(theme.radii)) {
      const path = `radii.${token}`;
      if (typeof value !== "number") {
        errors.push({
          severity: "error",
          path,
          message: "Radius value must be a number",
          value
        });
      } else if (value < 0) {
        warnings.push({
          severity: "warning",
          path,
          message: "Radius value is negative",
          value
        });
      }
    }
  }
  if (theme.shadows) {
    for (const [token, value] of Object.entries(theme.shadows)) {
      const path = `shadows.${token}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Shadow value must be a string",
          value
        });
      }
    }
  }
  if (theme.zIndices) {
    for (const [token, value] of Object.entries(theme.zIndices)) {
      const path = `zIndices.${token}`;
      if (typeof value !== "number" || !Number.isInteger(value)) {
        errors.push({
          severity: "error",
          path,
          message: "Z-index value must be an integer",
          value
        });
      }
    }
  }
  if (theme.textStyles) {
    const knownBreakpointKeys = new Set(["base", ...Object.keys(theme.breakpoints ?? {})]);
    for (const [styleName, definition] of Object.entries(theme.textStyles)) {
      const stylePath = `textStyles.${styleName}`;
      if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
        errors.push({
          severity: "error",
          path: stylePath,
          message: "Text style definition must be an object keyed by breakpoint",
          value: definition
        });
        continue;
      }
      for (const [breakpoint, properties] of Object.entries(definition)) {
        const breakpointPath = `${stylePath}.${breakpoint}`;
        if (!knownBreakpointKeys.has(breakpoint)) {
          errors.push({
            severity: "error",
            path: breakpointPath,
            message: `Unknown textStyle breakpoint '${breakpoint}'`,
            value: properties
          });
          continue;
        }
        if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
          errors.push({
            severity: "error",
            path: breakpointPath,
            message: "Text style breakpoint value must be an object",
            value: properties
          });
          continue;
        }
        for (const [propertyName, value] of Object.entries(properties)) {
          const propertyPath = `${breakpointPath}.${propertyName}`;
          if (!TEXT_STYLE_PROPERTY_NAME_SET.has(propertyName)) {
            errors.push({
              severity: "error",
              path: propertyPath,
              message: `Unsupported text style property '${propertyName}'`,
              value
            });
            continue;
          }
          if (typeof value !== "string" && typeof value !== "number") {
            errors.push({
              severity: "error",
              path: propertyPath,
              message: "Text style values must be strings or numbers",
              value
            });
            continue;
          }
          if (typeof value === "string" && RE_ANY_REF.test(value) && !canResolveReference(value, lookup)) {
            errors.push({
              severity: "error",
              path: propertyPath,
              message: `Cannot resolve textStyle reference: ${value}`,
              value
            });
          }
        }
      }
    }
  }
  if (theme.responsiveComponentTokens) {
    const knownBreakpointKeys = new Set(["base", ...Object.keys(theme.breakpoints ?? {})]);
    for (const [tokenPath, definition] of Object.entries(theme.responsiveComponentTokens)) {
      const path = `responsiveComponentTokens.${tokenPath}`;
      if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
        errors.push({
          severity: "error",
          path,
          message: "Responsive component token definition must be an object keyed by breakpoint",
          value: definition
        });
        continue;
      }
      for (const [breakpoint, value] of Object.entries(definition)) {
        const breakpointPath = `${path}.${breakpoint}`;
        if (!knownBreakpointKeys.has(breakpoint)) {
          errors.push({
            severity: "error",
            path: breakpointPath,
            message: `Unknown responsiveComponentTokens breakpoint '${breakpoint}'`,
            value
          });
          continue;
        }
        if (typeof value !== "string") {
          errors.push({
            severity: "error",
            path: breakpointPath,
            message: "Responsive component token values must be strings",
            value
          });
          continue;
        }
        if (RE_ANY_REF.test(value) && !canResolveReference(value, lookup)) {
          errors.push({
            severity: "error",
            path: breakpointPath,
            message: `Cannot resolve responsive component token reference: ${value}`,
            value
          });
        }
      }
    }
  }
  if (theme.tokens) {
    for (const [tokenPath, value] of Object.entries(theme.tokens)) {
      const path = `tokens.${tokenPath}`;
      if (typeof value !== "string") {
        errors.push({
          severity: "error",
          path,
          message: "Token override value must be a string",
          value
        });
        continue;
      }
      if (RE_ANY_REF.test(value) && !canResolveReference(value, lookup)) {
        errors.push({
          severity: "error",
          path,
          message: `Cannot resolve token override reference: ${value}`,
          value
        });
      }
    }
  }
  const circularPaths = detectCircularReferences(theme);
  for (const path of circularPaths) {
    errors.push({
      severity: "error",
      path,
      message: "Circular reference detected"
    });
  }
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}
function assertValidTheme(theme) {
  const result = validateTheme(theme);
  if (!result.valid) {
    const errorMessages = result.errors.map((e) => `  ${e.path}: ${e.message}`).join(`
`);
    throw new Error(`Theme validation failed:
${errorMessages}`);
  }
}

// src/theme/extend.ts
function extendTheme(overrides, options = {}) {
  const {
    validate: shouldValidate = true,
    resolveRefs = false,
    strict = false,
    baseTheme = defaultTheme
  } = options;
  let theme = mergeTheme(baseTheme, overrides);
  if (overrides.tokens) {
    theme = {
      ...theme,
      tokens: { ...theme.tokens, ...overrides.tokens }
    };
  }
  if (resolveRefs) {
    theme = resolveReferences(theme);
  }
  let validation;
  if (shouldValidate) {
    validation = validateTheme(theme);
    if (strict && !validation.valid) {
      const errorMessages = validation.errors.map((e) => `  ${e.path}: ${e.message}`).join(`
`);
      throw new Error(`Theme validation failed:
${errorMessages}`);
    }
  }
  return { theme, validation };
}
function createThemeVariant(semantic, options = {}) {
  const { theme } = extendTheme({ colors: semantic }, { ...options, resolveRefs: true });
  return theme;
}
function validateThemeOverrides(overrides) {
  const tempTheme = mergeTheme(defaultTheme, overrides);
  return validateTheme(tempTheme);
}

// src/theme/default-scopes.ts
var lightSemanticTheme = createThemeVariant(lightSemanticColors);
var darkSemanticTheme = createThemeVariant(darkSemanticColors);
var defaultSemanticThemeScopes = Object.freeze({
  defaultTheme: lightSemanticTheme,
  scopes: Object.freeze({
    dark: {
      selector: "[data-smth-theme='dark']",
      theme: darkSemanticTheme
    }
  })
});

// src/theme/errors.ts
var ThemeErrorCode;
((ThemeErrorCode2) => {
  ThemeErrorCode2["MISSING_PROPERTY"] = "MISSING_PROPERTY";
  ThemeErrorCode2["INVALID_TYPE"] = "INVALID_TYPE";
  ThemeErrorCode2["INVALID_COLOR_FORMAT"] = "INVALID_COLOR_FORMAT";
  ThemeErrorCode2["UNKNOWN_COLOR"] = "UNKNOWN_COLOR";
  ThemeErrorCode2["UNKNOWN_SHADE"] = "UNKNOWN_SHADE";
  ThemeErrorCode2["UNRESOLVABLE_REFERENCE"] = "UNRESOLVABLE_REFERENCE";
  ThemeErrorCode2["CIRCULAR_REFERENCE"] = "CIRCULAR_REFERENCE";
  ThemeErrorCode2["INVALID_REFERENCE_FORMAT"] = "INVALID_REFERENCE_FORMAT";
  ThemeErrorCode2["NEGATIVE_VALUE"] = "NEGATIVE_VALUE";
  ThemeErrorCode2["INVALID_VALUE"] = "INVALID_VALUE";
})(ThemeErrorCode ||= {});

class ThemeError extends Error {
  code;
  path;
  value;
  suggestion;
  constructor(code, path, message, options) {
    super(message);
    this.name = "ThemeError";
    this.code = code;
    this.path = path;
    this.value = options?.value;
    this.suggestion = options?.suggestion;
  }
  format() {
    const lines = [
      `[${this.code}] ${this.message}`,
      `  at: ${this.path}`
    ];
    if (this.value !== undefined) {
      lines.push(`  value: ${JSON.stringify(this.value)}`);
    }
    if (this.suggestion) {
      lines.push(`  suggestion: ${this.suggestion}`);
    }
    return lines.join(`
`);
  }
}
var ThemeErrors = {
  missingProperty(property) {
    return new ThemeError("MISSING_PROPERTY" /* MISSING_PROPERTY */, property, `Missing required theme property: '${property}'`, {
      suggestion: `Add the '${property}' property to your theme configuration.`
    });
  },
  invalidColorFormat(path, value) {
    const isAlmostHex = /^#?[0-9a-fA-F]{3,8}$/.test(value.trim());
    let suggestion = "Use hex (#RRGGBB), rgb(), rgba(), or hsl() format.";
    if (isAlmostHex && !value.startsWith("#")) {
      suggestion = `Did you mean '#${value}'?`;
    }
    return new ThemeError("INVALID_COLOR_FORMAT" /* INVALID_COLOR_FORMAT */, path, `Invalid color format at '${path}'`, { value, suggestion });
  },
  unknownColor(path, colorName, availableColors) {
    const similar = findSimilar(colorName, availableColors);
    let suggestion = `Available colors: ${availableColors.join(", ")}`;
    if (similar) {
      suggestion = `Did you mean '${similar}'? ${suggestion}`;
    }
    return new ThemeError("UNKNOWN_COLOR" /* UNKNOWN_COLOR */, path, `Unknown color: '${colorName}'`, { value: colorName, suggestion });
  },
  unknownShade(path, shade, availableShades) {
    return new ThemeError("UNKNOWN_SHADE" /* UNKNOWN_SHADE */, path, `Unknown shade: '${shade}'`, {
      value: shade,
      suggestion: `Available shades: ${availableShades.join(", ")}`
    });
  },
  unresolvableReference(path, reference) {
    const match = reference.match(/^\{colors\.(\w+)\.(\w+)\}$/);
    let suggestion = "Check that the referenced token exists in the theme.";
    if (match) {
      const [, color, shade] = match;
      suggestion = `Verify that '${color}' exists in colors and has a '${shade}' shade.`;
    }
    return new ThemeError("UNRESOLVABLE_REFERENCE" /* UNRESOLVABLE_REFERENCE */, path, `Cannot resolve token reference: ${reference}`, { value: reference, suggestion });
  },
  circularReference(path, chain) {
    return new ThemeError("CIRCULAR_REFERENCE" /* CIRCULAR_REFERENCE */, path, `Circular reference detected`, {
      value: chain,
      suggestion: `Reference chain: ${chain.join(" -> ")}. Break the cycle.`
    });
  },
  invalidReferenceFormat(path, value) {
    return new ThemeError("INVALID_REFERENCE_FORMAT" /* INVALID_REFERENCE_FORMAT */, path, `Invalid token reference format: '${value}'`, {
      value,
      suggestion: "Use the format '{category.path.to.token}', e.g., '{colors.blue.500}'"
    });
  },
  negativeValue(path, value) {
    return new ThemeError("NEGATIVE_VALUE" /* NEGATIVE_VALUE */, path, `Negative values are not allowed for '${path}'`, {
      value,
      suggestion: `Use a positive value. Got: ${value}`
    });
  }
};
function findSimilar(input, candidates) {
  const inputLower = input.toLowerCase();
  let bestMatch = null;
  let bestScore = 0;
  for (const candidate of candidates) {
    const candidateLower = candidate.toLowerCase();
    if (candidateLower.startsWith(inputLower) || candidateLower.includes(inputLower)) {
      const score = inputLower.length / candidateLower.length;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = candidate;
      }
    }
    let overlap = 0;
    for (const char of inputLower) {
      if (candidateLower.includes(char)) {
        overlap++;
      }
    }
    const overlapScore = overlap / Math.max(inputLower.length, candidateLower.length);
    if (overlapScore > 0.6 && overlapScore > bestScore) {
      bestScore = overlapScore;
      bestMatch = candidate;
    }
  }
  return bestScore > 0.5 ? bestMatch : null;
}
function formatThemeErrors(errors) {
  if (errors.length === 0) {
    return "No errors";
  }
  const header = `Found ${errors.length} theme error${errors.length > 1 ? "s" : ""}:
`;
  const formatted = errors.map((e, i) => `${i + 1}. ${e.format()}`).join(`

`);
  return header + formatted;
}

// src/theme/bundle-entry.ts
var assertValidTheme2 = assertValidTheme;
var compileThemeCss2 = compileThemeCss;
var createThemeVariant2 = createThemeVariant;
var darkSemanticTheme2 = darkSemanticTheme;
var defaultSemanticThemeScopes2 = defaultSemanticThemeScopes;
var defaultTextStyles2 = defaultTextStyles;
var defaultTheme2 = defaultTheme;
var DEFAULT_TEXT_STYLE_ORDER2 = DEFAULT_TEXT_STYLE_ORDER;
var extendTheme2 = extendTheme;
var formatThemeErrors2 = formatThemeErrors;
var lightSemanticTheme2 = lightSemanticTheme;
var TEXT_STYLE_PROPERTY_NAMES2 = TEXT_STYLE_PROPERTY_NAMES;
var ThemeError2 = ThemeError;
var ThemeErrorCode2 = ThemeErrorCode;
var ThemeErrors2 = ThemeErrors;
var validateTheme2 = validateTheme;
var validateThemeOverrides2 = validateThemeOverrides;
export {
  DEFAULT_TEXT_STYLE_ORDER2 as DEFAULT_TEXT_STYLE_ORDER,
  TEXT_STYLE_PROPERTY_NAMES2 as TEXT_STYLE_PROPERTY_NAMES,
  ThemeError2 as ThemeError,
  ThemeErrorCode2 as ThemeErrorCode,
  ThemeErrors2 as ThemeErrors,
  assertValidTheme2 as assertValidTheme,
  compileThemeCss2 as compileThemeCss,
  createThemeVariant2 as createThemeVariant,
  darkSemanticTheme2 as darkSemanticTheme,
  defaultSemanticThemeScopes2 as defaultSemanticThemeScopes,
  defaultTextStyles2 as defaultTextStyles,
  defaultTheme2 as defaultTheme,
  extendTheme2 as extendTheme,
  formatThemeErrors2 as formatThemeErrors,
  lightSemanticTheme2 as lightSemanticTheme,
  validateTheme2 as validateTheme,
  validateThemeOverrides2 as validateThemeOverrides
};
